import React from "react";
import * as XLSX from "xlsx-js-style";
import { IPayrollReport } from "../../models/Payroll";
import { getCurrencyFormat } from "../../utils/index.ts";
import { calcularTiempoTrabajado } from "../../interfaces/formik/IReporteRelojChecador.ts";
import {
  formatHours,
  formatHoursToTime,
  formatPayrollHoursDecimal,
  formatPayrollHoursWithH,
  calculateDailyPayment,
  getDailyPaymentConcept,
  calculateExtraHoursLikeChart,
  roundHours,
} from "../../services/payroll/ui.ts";
import {
  calculateDoubleShiftPay,
  calculateVacationPay,
  FREE_DAY_WORKED_PREMIUM_RATE,
  hasAcceptedFreeDayWorkedIncident,
  withFreeDayWorkedPremium,
} from "../../services/payroll/payPremiums.ts";
import { formatDataForReport } from "../../utils/reporteChequeoUtils.ts";
import { formatPayrollDisplayDate } from "../../utils/payrollDateFormat.ts";
import * as moment from "moment-timezone";
import { useCalculateTotalPay } from "../../hooks/useCalculateTotalPay.tsx";
import { useDispatch } from "react-redux";
import { updateRecordByIndex } from "../../store/slices/payrollSlice.ts";
import { Tooltip } from "@mui/material";
import {
  buildAbsenceOtrosIngresoRows,
  getPartnerHoursByDate,
  getReciboJornadasCompletasCuadroDisplay,
  groupDeductionsForRecibo,
  isAcceptedExtraRelatedIncident,
  isReciboRestDayEnjoyedRow,
  isReciboRestDayWorkedRow,
  isDoubleShiftRegisteredHours,
  buildScheduleForPaymentDay,
  mergeIncidentsForPaymentDay,
  parseDeductionAmount,
  parsePayrollMoneyValue,
  resolvePayrollDeductionTotals,
  resolvePayrollIncentivesTotal,
  paymentDetailRowDateKeyDDMMYYYY,
  scheduleHasRejectedExtraPayIncident,
  subtractRejectedExtraHours,
  summarizeChartDays,
  sumExtraHoursFromDailyPayments,
  countDailyPaymentsWithAcceptedExtras,
} from "../../utils/payrollUtils.ts";
import { appendMultiVisitExtrasToConcept } from "../../utils/payrollMultiVisitExtras.ts";
import { effectivePayrollDayVisits } from "../../utils/payrollUnscheduledIncident.ts";
import {
  findPaymentDetail,
  findChartDaysWorked,
  findTotalIncidents,
  upsertDailyPaymentOverride,
} from "../../services/payroll.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { useFullDayHours } from "../../hooks/useFullDayHours.tsx";
import { usePayrollBranchMeta } from "../../hooks/usePayrollBranchMeta.ts";
import { getIncapacitiesByPartner } from "../../services/incapacities.service.ts";
import { getPermissionsByPartner } from "../../services/permissions.service.ts";
import {
  filterIncidentsInPayrollDateRange,
} from "../../utils/incidentLeaderReviewFilter.ts";
import {
  adjustChartDetailedPayrollExtras,
  buildPayrollRhPendingFromSnapshot,
} from "../../utils/payrollRhPending.ts";
import type { PartnerIncidentCountSnapshot } from "../../services/incident.service.ts";
import {
  appendMissingChequeoDaysToDailyPayments,
  buildPartnerVisitHoursByFecha,
  enrichVisitsWithBranchNames,
  mapStoredVisit,
  mergeDailyPaymentsByDate,
  normalizePaymentDateKey,
} from "../../utils/payrollVisitHours.ts";
import PayrollDayVisitRows, {
  PayrollDayVisitDetailRows,
  PayrollDayVisitTotalRow,
} from "../../components/nomina/PayrollDayVisitRows.tsx";

const PENDING_LEADER_INCIDENT_TOOLTIP =
  "Incidencia o chequeo no programado pendiente de resolver en RH.";

function PendingLeaderIncidentDot() {
  return (
    <Tooltip title={PENDING_LEADER_INCIDENT_TOOLTIP} arrow>
      <span
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#f59e0b",
          marginLeft: 6,
          verticalAlign: "middle",
          cursor: "help",
        }}
        aria-label="Incidencia pendiente de revisión del líder"
      />
    </Tooltip>
  );
}

const getConceptos = (getCelebrityDaysTotalFromBreakdown: () => number, fullDayHours: number = 8.5) => [
  {
    key: "hourlyPay",
    label: "Salario por hora",
    value: (payment: any, data: any, fullDayHours: number = 8.5) => {
      const dailySalary =
        Number(data.dailySalary.replace(/[^0-9.-]+/g, "")) || 0;
      const hourlyPay = dailySalary / fullDayHours;
      return getCurrencyFormat(hourlyPay);
    },
    money: (payment: any) => "",
  },
  {
    key: "dailySalary",
    label: "Salario diario base",
    value: (payment: any, data: any, fullDayHours: number = 8.5) => `${fullDayHours} horas`,
    money: (payment: any, data: any) => data.dailySalary,
  },
  {
    key: "hoursAuthorizedPay",
    label: "Pago por horas autorizadas",
    value: (payment: any) => "",
    money: (payment: any) => payment.hoursAuthorizedPay,
  },
  {
    key: "doubleShiftPayTotal",
    label: "Pago por turnos dobles",
    value: (payment: any) => formatHours(payment.doubleShiftsHours || 0),
    money: (payment: any) => payment.doubleShiftPayTotal || "$0.00",
  },
  {
    key: "extraHourPayTotal",
    label: "Pago por horas extra",
    value: (payment: any) => formatHours(payment.extraHours || 0),
    money: (payment: any) => payment.extraHourPayTotal || "$0.00",
  },
  {
    key: "freeDayWorkedPayTotal",
    label: "Pago por días de descanso trabajados",
    value: (payment: any) => payment.freeDaysWorked || 0,
    money: (payment: any) => payment.freeDayWorkedPayTotal || "$0.00",
  },
  {
    key: "freeDaysEnjoyedPay",
    label: "Pago por días de descanso disfrutados",
    value: (payment: any) => payment.freeDaysEnjoyed,
    money: (payment: any) => payment.freeDaysEnjoyedPay,
  },
  {
    key: "celebrityDaysPayTotal",
    label: "Días festivos trabajados",
    value: (payment: any) =>
      `${payment.celebrityDaysWorked || 0} día${
        (payment.celebrityDaysWorked || 0) !== 1 ? "s" : ""
      }`,
    money: (payment: any) =>
      getCurrencyFormat(getCelebrityDaysTotalFromBreakdown()),
  },
  {
    key: "totalIncentives",
    label: "Incentivos",
    value: (payment: any) => "",
    money: (payment: any) => payment.totalIncentives,
  },
  {
    key: "totalDeductions",
    label: "Deducciones",
    value: (payment: any) => "",
    money: (payment: any) => payment.totalDeductions,
  },
];

const PaymentDetail = ({
  data,
  detailedRecords,
  appliedBonuses = [],
  appliedMissingBoxes = [],
  appliedNewDeductions = [],
  dates,
  realCheckRecords,
  partner,
  branch,
  chartDaysDetailed,
  chartDaysGraph,
  chartDaysSavedIncidents,
  pendingLeaderReviewDates: pendingLeaderReviewDatesProp,
  incidentCountSnapshot,
  initialFetchedData,
  initialFetchedDataVersion,
  deferNetworkFetch = false,
  onOverridesSaved,
}: {
  data: IPayrollReport;
  detailedRecords?: any[];
  appliedBonuses?: any[];
  appliedMissingBoxes?: any[];
  appliedNewDeductions?: any[];
  dates?: { start: any; end: any };
  realCheckRecords?: any[];
  partner?: number;
  branch?: number;
  chartDaysDetailed?: any[];
  chartDaysGraph?: any[];
  /** Incidencias del gráfico (finalStatus actualizado tras aprobar/rechazar). */
  chartDaysSavedIncidents?: any[];
  /** Desde PayrollBranch (batch); evita GET /incident/partner-count por fila. */
  pendingLeaderReviewDates?: string[];
  incidentCountSnapshot?: PartnerIncidentCountSnapshot | null;
  /** Precargado por RowPayRoll al abrir el modal (evita GET /payroll/payment-detail duplicado). */
  initialFetchedData?: {
    dailyPayments?: any[];
    realCheckRecords?: any[];
    schedules?: any[];
    realSchedule?: any[];
    paymentDetails?: any;
    calculatedBonuses?: any[];
    calculatedNewDeductions?: any[];
    freeDaysEnjoyedDates?: string[];
    absenceRows?: any[];
  } | null;
  /** Incrementado por RowPayRoll tras aprobar/rechazar incidencias para forzar sync. */
  initialFetchedDataVersion?: number;
  /** true = el batch del cuadro aún no entrega detalle; no pedir payment-detail/chart. */
  deferNetworkFetch?: boolean;
  onOverridesSaved?: (payload: {
    workDate: string;
    payment: number;
    concept: string;
  }) => void | Promise<void>;
}) => {
  const dispatch = useDispatch();
  const { paymentDetails, realSchedule } = data;
  const preferLocalFetchedDataRef = React.useRef(false);
  const isPreloadComplete = React.useMemo(
    () => Boolean(initialFetchedData?.dailyPayments?.length),
    [initialFetchedData]
  );
  const [loading, setLoading] = React.useState(
    () =>
      !isPreloadComplete &&
      !deferNetworkFetch &&
      Boolean(partner && branch && dates)
  );
  const { fullDayHours: fullDayHoursFromHook } = useFullDayHours(branch);
  // Asegurar que fullDayHours siempre sea un número válido
  const fullDayHours = React.useMemo(() => {
    const value = typeof fullDayHoursFromHook === 'number' && !isNaN(fullDayHoursFromHook) && fullDayHoursFromHook > 0
      ? fullDayHoursFromHook
      : 8.5;
    return value;
  }, [fullDayHoursFromHook]);
  const [fetchedData, setFetchedData] = React.useState<{
    dailyPayments?: any[];
    realCheckRecords?: any[];
    schedules?: any[];
    realSchedule?: any[];
    paymentDetails?: any;
    calculatedBonuses?: any[];
    calculatedNewDeductions?: any[];
    freeDaysEnjoyedDates?: string[];
    absenceRows?: any[];
  } | null>(() =>
    initialFetchedData?.dailyPayments?.length ? initialFetchedData : null
  );
  const [chartDaysData, setChartDaysData] = React.useState<{
    detailed: any[];
    graph: any[];
    freeDaysDates?: string[];
    savedIncidents?: any[];
  }>({
    detailed: chartDaysDetailed || [],
    graph: chartDaysGraph || [],
    freeDaysDates: [],
    savedIncidents: chartDaysSavedIncidents || [],
  });

  const allSavedIncidents = React.useMemo(
    () => chartDaysSavedIncidents || chartDaysData?.savedIncidents || [],
    [chartDaysSavedIncidents, chartDaysData?.savedIncidents]
  );
  const [incapacityRows, setIncapacityRows] = React.useState<any[]>([]);
  const [permissionDates, setPermissionDates] = React.useState<string[]>([]);
  const [pendingLeaderReviewDates, setPendingLeaderReviewDates] = React.useState<
    Set<string>
  >(() => new Set());
  React.useEffect(() => {
    if (chartDaysDetailed) {
      setChartDaysData((prev) => ({
        ...prev,
        detailed: chartDaysDetailed,
      }));
    }
  }, [chartDaysDetailed]);

  React.useEffect(() => {
    if (chartDaysGraph) {
      setChartDaysData((prev) => ({
        ...prev,
        graph: chartDaysGraph,
      }));
    }
  }, [chartDaysGraph]);

  React.useEffect(() => {
    if (chartDaysSavedIncidents?.length) {
      setChartDaysData((prev) => ({
        ...prev,
        savedIncidents: chartDaysSavedIncidents,
      }));
    }
  }, [chartDaysSavedIncidents]);

  React.useEffect(() => {
    if (partner && branch && dates && !chartDaysDetailed?.length && !chartDaysGraph?.length) {
      const fetchFreeDaysDates = async () => {
        try {
          const startDate = dates.start.format("YYYY-MM-DD");
          const endDate = dates.end.format("YYYY-MM-DD");
          const chartResponse = await findChartDaysWorked(
            partner,
            branch,
            startDate,
            endDate
          );
          setChartDaysData((prev) => ({
            ...prev,
            freeDaysDates: chartResponse.freeDaysDates || [],
          }));
        } catch (error) {
          console.error("Error fetching free days dates:", error);
        }
      };
      fetchFreeDaysDates();
    }
  }, [partner, branch, dates, chartDaysDetailed, chartDaysGraph]);

  const prevPartnerRef = React.useRef(partner);
  React.useEffect(() => {
    if (prevPartnerRef.current !== partner) {
      preferLocalFetchedDataRef.current = false;
      prevPartnerRef.current = partner;
    }
  }, [partner]);

  const lastParentDataVersionRef = React.useRef(0);

  React.useEffect(() => {
    if (
      initialFetchedDataVersion != null &&
      initialFetchedDataVersion > lastParentDataVersionRef.current
    ) {
      lastParentDataVersionRef.current = initialFetchedDataVersion;
      if (initialFetchedData?.dailyPayments?.length) {
        setFetchedData({
          dailyPayments: initialFetchedData.dailyPayments || [],
          realCheckRecords: initialFetchedData.realCheckRecords || [],
          schedules: initialFetchedData.schedules || [],
          realSchedule: initialFetchedData.realSchedule || [],
          paymentDetails: initialFetchedData.paymentDetails || {},
          calculatedBonuses: initialFetchedData.calculatedBonuses || [],
          calculatedNewDeductions:
            initialFetchedData.calculatedNewDeductions || [],
          freeDaysEnjoyedDates: initialFetchedData.freeDaysEnjoyedDates || [],
          absenceRows: initialFetchedData.absenceRows || [],
        });
        preferLocalFetchedDataRef.current = true;
        setLoading(false);
        return;
      }
      preferLocalFetchedDataRef.current = false;
    }
    if (preferLocalFetchedDataRef.current) {
      return;
    }
    if (initialFetchedData?.dailyPayments?.length) {
      setFetchedData({
        dailyPayments: initialFetchedData.dailyPayments || [],
        realCheckRecords: initialFetchedData.realCheckRecords || [],
        schedules: initialFetchedData.schedules || [],
        realSchedule: initialFetchedData.realSchedule || [],
        paymentDetails: initialFetchedData.paymentDetails || {},
        calculatedBonuses: initialFetchedData.calculatedBonuses || [],
        calculatedNewDeductions:
          initialFetchedData.calculatedNewDeductions || [],
        freeDaysEnjoyedDates: initialFetchedData.freeDaysEnjoyedDates || [],
        absenceRows: initialFetchedData.absenceRows || [],
      });
      preferLocalFetchedDataRef.current = true;
      setLoading(false);
      return;
    }
    if (deferNetworkFetch) {
      setLoading(true);
      return;
    }
    if (preferLocalFetchedDataRef.current) {
      return;
    }
    if (partner && branch && dates && !isPreloadComplete) {
      void fetchPaymentDetailData();
    }
  }, [
    partner,
    branch,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    initialFetchedData,
    initialFetchedDataVersion,
    isPreloadComplete,
    deferNetworkFetch,
  ]);

  React.useEffect(() => {
    const fetchIncapacitiesForRange = async () => {
      if (!partner || !dates?.start || !dates?.end) {
        setIncapacityRows([]);
        return;
      }

      try {
        const [incapacityList, permissionList] = await Promise.all([
          getIncapacitiesByPartner(partner),
          getPermissionsByPartner(partner).catch(() => []),
        ]);
        const startRange = moment(dates.start.format("YYYY-MM-DD"), "YYYY-MM-DD").startOf("day");
        const endRange = moment(dates.end.format("YYYY-MM-DD"), "YYYY-MM-DD").endOf("day");
        const rows: any[] = [];
        const usedDates = new Set<string>();
        const permissionDatesSet = new Set<string>();

        (incapacityList || [])
          .filter((inc: any) => inc?.approved && !inc?.isRejected && inc?.startDate && inc?.endDate)
          .forEach((inc: any) => {
            const incStart = moment.unix(Number(inc.startDate)).startOf("day");
            const incEnd = moment.unix(Number(inc.endDate)).endOf("day");
            const overlapStart = moment.max(incStart, startRange.clone());
            const overlapEnd = moment.min(incEnd, endRange.clone());

            if (overlapStart.isAfter(overlapEnd)) return;

            const cursor = overlapStart.clone().startOf("day");
            while (cursor.isSameOrBefore(overlapEnd, "day")) {
              const normalizedDate = cursor.format("YYYY-MM-DD");
              if (!usedDates.has(normalizedDate)) {
                rows.push({
                  date: cursor.format("DD/MM/YYYY"),
                  normalizedDate,
                  hours: "0.00",
                  hoursInSchedule: "0.00",
                  hoursForDisplay: 0,
                  concept: "Incapacidad",
                  payment: 0,
                  checkIn: "N/A",
                  checkOut: "N/A",
                  isFreeDayWorked: false,
                  isFreeDayEnjoyed: false,
                  isIncapacity: true,
                  hasPaymentOverride: false,
                });
                usedDates.add(normalizedDate);
              }
              cursor.add(1, "day");
            }
          });

        (permissionList || [])
          .filter(
            (permission: any) =>
              permission?.approved &&
              !permission?.isRejected &&
              permission?.startRequested &&
              permission?.endRequested
          )
          .forEach((permission: any) => {
            const permissionStart = moment
              .unix(Number(permission.startRequested))
              .startOf("day");
            const permissionEnd = moment
              .unix(Number(permission.endRequested))
              .endOf("day");
            const overlapStart = moment.max(permissionStart, startRange.clone());
            const overlapEnd = moment.min(permissionEnd, endRange.clone());
            if (overlapStart.isAfter(overlapEnd)) return;

            const cursor = overlapStart.clone().startOf("day");
            while (cursor.isSameOrBefore(overlapEnd, "day")) {
              permissionDatesSet.add(cursor.format("YYYY-MM-DD"));
              cursor.add(1, "day");
            }
          });

        rows.sort((a, b) =>
          moment(a.normalizedDate, "YYYY-MM-DD").valueOf() -
          moment(b.normalizedDate, "YYYY-MM-DD").valueOf()
        );
        setIncapacityRows(rows);
        setPermissionDates(Array.from(permissionDatesSet));
      } catch (error) {
        console.error("Error fetching incapacities for payroll detail:", error);
        setIncapacityRows([]);
        setPermissionDates([]);
      }
    };

    fetchIncapacitiesForRange();
  }, [partner, dates?.start?.format("YYYY-MM-DD"), dates?.end?.format("YYYY-MM-DD")]);

  const fetchPaymentDetailData = async (opts?: {
    silent?: boolean;
    refresh?: boolean;
  }) => {
    if (!partner || !branch || !dates) return;

    const silent = opts?.silent === true;
    const forceRefresh = opts?.refresh === true;
    if (
      !forceRefresh &&
      (initialFetchedData?.dailyPayments?.length ||
        fetchedData?.dailyPayments?.length)
    ) {
      if (!silent) setLoading(false);
      return;
    }

    if (!silent) setLoading(true);
    try {
      const startDate = dates.start.format("YYYY-MM-DD");
      const endDate = dates.end.format("YYYY-MM-DD");

      const response = await findPaymentDetail(
        partner,
        branch,
        startDate,
        endDate,
        undefined,
        { refresh: opts?.refresh === true }
      );

      setFetchedData({
        dailyPayments: response.dailyPayments || [],
        realCheckRecords: response.realCheckRecords || [],
        schedules: response.schedules || [],
        realSchedule: response.realSchedule || [],
        paymentDetails: response.paymentDetails || {},
        calculatedBonuses: response.calculatedBonuses || [],
        calculatedNewDeductions: response.calculatedNewDeductions || [],
        freeDaysEnjoyedDates: response.freeDaysEnjoyedDates || [],
        absenceRows: response.absenceRows || [],
      });
      preferLocalFetchedDataRef.current = true;

      if (silent && chartDaysDetailed) {
        try {
          const chartResponse = await findChartDaysWorked(
            partner,
            branch,
            startDate,
            endDate,
            undefined,
            { refresh: true }
          );
          const adjustedDetailed = adjustChartDetailedPayrollExtras(
            chartResponse.detailed || [],
            chartResponse.savedIncidents || [],
            incidentCountSnapshot
          );
          const absenceDetailed = (chartResponse.absenceRows || []).map(
            (row: any) => ({
              id: 0,
              fecha: row.date,
              entradaProgramada: row.entradaProgramada || "N/A",
              salidaProgramada: row.salidaProgramada || "N/A",
              entradaRegistrada: "N/A",
              salidaRegistrada: "N/A",
              horasTrabajadasEnHorario: 0,
              horasRegistradas: 0,
              horasExtras: 0,
              isAbsence: true,
            })
          );
          const detailedWithAbsences = [
            ...adjustedDetailed,
            ...absenceDetailed,
          ].sort(
            (a, b) =>
              moment(a.fecha, "DD-MM-YYYY").valueOf() -
              moment(b.fecha, "DD-MM-YYYY").valueOf()
          );
          setChartDaysData((prev) => ({
            ...prev,
            detailed: detailedWithAbsences,
            graph: chartResponse.graph || prev.graph,
            freeDaysDates: chartResponse.freeDaysDates || prev.freeDaysDates,
            savedIncidents:
              chartResponse.savedIncidents || prev.savedIncidents || [],
          }));
        } catch (chartError) {
          console.error("Error refreshing chart after save:", chartError);
        }
      }

      if (!silent && !chartDaysDetailed?.length && !chartDaysGraph?.length) {
        try {
          const [chartResponse, incidentsResponse] = await Promise.all([
            findChartDaysWorked(partner, branch, startDate, endDate),
            findTotalIncidents(partner, branch, startDate, endDate).catch(
              () => ({ incidents: [] })
            ),
          ]);
          const fetchedIncidents = incidentsResponse?.incidents || [];
          const visibleIncidents = filterIncidentsInPayrollDateRange(
            fetchedIncidents,
            dates.start.format("YYYY-MM-DD"),
            dates.end.format("YYYY-MM-DD")
          );
          const adjustedDetailed = adjustChartDetailedPayrollExtras(
            chartResponse.detailed || [],
            visibleIncidents,
            incidentCountSnapshot
          );
          setChartDaysData({
            detailed: adjustedDetailed,
            graph: chartResponse.graph || [],
            freeDaysDates: chartResponse.freeDaysDates || [],
            savedIncidents:
              chartResponse.savedIncidents || fetchedIncidents || [],
          });
        } catch (chartError) {
          console.error("Error fetching chart days worked:", chartError);
        }
      }
    } catch (error) {
      console.error("Error fetching payment detail data:", error);
      if (!silent) {
        notification("Error ⚠️", "Error al obtener los datos de pago", "danger");
      } else {
        throw error;
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const effectiveRealCheckRecords =
    fetchedData?.realCheckRecords || realCheckRecords || [];
  const payrollBranchMeta = usePayrollBranchMeta(
    effectiveRealCheckRecords,
    branch
  );
  const effectiveSchedules = fetchedData?.schedules || data.schedules || [];
  const effectiveRealSchedule = fetchedData?.realSchedule || realSchedule || [];

  React.useEffect(() => {
    if (!dates?.start || !dates?.end) {
      setPendingLeaderReviewDates(new Set());
      return;
    }
    const visibleIncidents = filterIncidentsInPayrollDateRange(
      allSavedIncidents,
      dates.start.format("YYYY-MM-DD"),
      dates.end.format("YYYY-MM-DD")
    );
    const { rhPending } = buildPayrollRhPendingFromSnapshot(
      visibleIncidents,
      incidentCountSnapshot
    );
    if (pendingLeaderReviewDatesProp?.length) {
      for (const key of pendingLeaderReviewDatesProp) {
        rhPending.add(key);
      }
    }
    setPendingLeaderReviewDates(rhPending);
  }, [
    allSavedIncidents,
    incidentCountSnapshot,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    pendingLeaderReviewDatesProp?.join("|"),
  ]);

  const chartSummary = React.useMemo(
    () => summarizeChartDays(chartDaysData?.detailed || []),
    [chartDaysData?.detailed]
  );
  const chartDetailMap = React.useMemo(() => {
    const map = new Map<string, any>();
    (chartDaysData?.detailed || []).forEach((item: any) => {
      if (item?.fecha) {
        map.set(item.fecha, item);
      }
    });
    return map;
  }, [chartDaysData?.detailed]);

  const { totalPayFormatted } = useCalculateTotalPay({
    data: {
      ...data,
      schedules: effectiveSchedules,
      realSchedule: effectiveRealSchedule,
    },
    detailedRecords,
    dates,
    realCheckRecords: effectiveRealCheckRecords,
    fullDayHours,
  });

  const effectivePaymentDetailSource = React.useMemo(
    () =>
      fetchedData?.dailyPayments?.length
        ? fetchedData
        : initialFetchedData?.dailyPayments?.length
          ? initialFetchedData
          : null,
    [fetchedData, initialFetchedData]
  );

  const resolvedBonusDeductionTotals = React.useMemo(() => {
    const totalIncentivesNumber = resolvePayrollIncentivesTotal(
      data,
      effectivePaymentDetailSource ?? undefined
    );
    const {
      totalDeductions: catalogDeductions,
      totalMissingBoxDeductions,
      uniformDeduction,
    } = resolvePayrollDeductionTotals(data, effectivePaymentDetailSource ?? undefined);
    const totalDeductionsCombined =
      catalogDeductions + totalMissingBoxDeductions + uniformDeduction;
    return {
      totalIncentivesNumber,
      catalogDeductions,
      totalMissingBoxDeductions,
      uniformDeduction,
      totalDeductionsCombined,
      totalIncentivesFormatted: getCurrencyFormat(totalIncentivesNumber),
      totalDeductionsFormatted: getCurrencyFormat(totalDeductionsCombined),
    };
  }, [data, effectivePaymentDetailSource]);

  const payment = React.useMemo(
    () => ({
      ...paymentDetails,
      dailySalary: data.dailySalary,
      totalDeductions: resolvedBonusDeductionTotals.totalDeductionsFormatted,
      totalIncentives: resolvedBonusDeductionTotals.totalIncentivesFormatted,
      totalPayWithIncentives: data.totalPayWithIncentives,
      celebrityDaysWorked: data.celebrityDaysWorked || 0,
      celebrityDaysPayTotal: data.celebrityDaysPayTotal || "$0.00",
      valorPagarHoras: data.totalHoursWorked,
    }),
    [paymentDetails, data, resolvedBonusDeductionTotals]
  );
  const [activeTab, setActiveTab] = React.useState("desglose");
  const [isConceptsExpanded, setIsConceptsExpanded] = React.useState(false);
  const [editingRowIndex, setEditingRowIndex] = React.useState<number | null>(
    null
  );
  const [editedPayments, setEditedPayments] = React.useState<{
    [key: number]: { concept: string; payment: number; isCustom?: boolean };
  }>({});
  const [savingRowIndex, setSavingRowIndex] = React.useState<number | null>(
    null
  );
  const [expandedPaymentDays, setExpandedPaymentDays] = React.useState<
    Set<string>
  >(() => new Set());

  const paymentPeriodStart = dates?.start?.format?.("YYYY-MM-DD") ?? "";
  const paymentPeriodEnd = dates?.end?.format?.("YYYY-MM-DD") ?? "";

  const visitsByFecha = React.useMemo(
    () =>
      buildPartnerVisitHoursByFecha(
        effectiveRealCheckRecords,
        data.partnerId,
        paymentPeriodStart,
        paymentPeriodEnd,
        payrollBranchMeta
      ),
    [
      effectiveRealCheckRecords,
      data.partnerId,
      paymentPeriodStart,
      paymentPeriodEnd,
      payrollBranchMeta,
    ]
  );

  const togglePaymentDayExpanded = (dateKey: string) => {
    React.startTransition(() => {
      setExpandedPaymentDays((prev) => {
        const next = new Set(prev);
        if (next.has(dateKey)) {
          next.delete(dateKey);
        } else {
          next.add(dateKey);
        }
        return next;
      });
    });
  };

  const handleSaveDailyPaymentOverride = async (
    index: number,
    day: {
      date: string;
      checkIn?: string;
      checkOut?: string;
      isFreeDayEnjoyed?: boolean;
    }
  ) => {
    if (!day) return;
    if (!partner || !branch || !dates) return;
    const editedData = editedPayments[index];
    if (!editedData) return;

    setSavingRowIndex(index);
    try {
      const startDate = dates.start.format("YYYY-MM-DD");
      const endDate = dates.end.format("YYYY-MM-DD");
      const workDate = moment(day.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).format(
        "YYYY-MM-DD"
      );
      const apiCheckIn =
        !day.checkIn || day.checkIn === "N/A" ? "" : String(day.checkIn);
      const apiCheckOut =
        !day.checkOut || day.checkOut === "N/A" ? "" : String(day.checkOut);

      const paymentToSave = Number(editedData.payment);
      if (!Number.isFinite(paymentToSave)) {
        notification("Error ⚠️", "Monto de pago inválido", "danger");
        return;
      }

      await upsertDailyPaymentOverride({
        partner,
        branch,
        periodStart: startDate,
        periodEnd: endDate,
        workDate,
        checkIn: apiCheckIn,
        checkOut: apiCheckOut,
        customConcept: editedData.concept,
        paymentAmount: paymentToSave,
      });

      preferLocalFetchedDataRef.current = true;
      setFetchedData((prev) => {
        if (!prev) return prev;
        const nextPayments = [...(prev.dailyPayments || [])];
        const matchIndex = nextPayments.findIndex((dp: any) => {
          const dpDate = moment(dp.date, [
            "YYYY-MM-DD",
            "DD/MM/YYYY",
            "DD-MM-YYYY",
          ]).format("YYYY-MM-DD");
          return dpDate === workDate;
        });
        const overrideFields = {
          payment: paymentToSave,
          conceptOverride: editedData.concept,
          hasPaymentOverride: true,
        };
        if (matchIndex >= 0) {
          nextPayments[matchIndex] = {
            ...nextPayments[matchIndex],
            ...overrideFields,
          };
        } else {
          nextPayments.push({
            date: workDate,
            hours: 0,
            hoursInSchedule: 0,
            payment: paymentToSave,
            conceptOverride: editedData.concept,
            hasPaymentOverride: true,
            checkIn: apiCheckIn || "N/A",
            checkOut: apiCheckOut || "N/A",
          });
        }
        return { ...prev, dailyPayments: nextPayments };
      });

      setEditedPayments((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setEditingRowIndex(null);
      notification(
        "Éxito",
        "Cambios guardados en el desglose diario",
        "success"
      );
      await onOverridesSaved?.({
        workDate,
        payment: paymentToSave,
        concept: editedData.concept,
      });
    } catch (error) {
      console.error("Error saving daily payment override:", error);
      notification("Error ⚠️", "No se pudo guardar el desglose", "danger");
    } finally {
      setSavingRowIndex(null);
    }
  };

  const dailySalaryNum = Number(
    String(data.dailySalary ?? "").replace(/[^0-9.-]+/g, "") || 0
  );
  const hourlyPayNum = Number(
    String(data.payPerHour ?? "").replace(/[^0-9.-]+/g, "") || 0
  );

  const conceptOptions = React.useMemo(() => [
    {
      value: `${fullDayHours}h (jornada completa)`,
      label: `Jornada completa (${fullDayHours}h)`,
      hours: fullDayHours,
      factor: 1,
      type: "jornada",
    },
    {
      value: "1h × pago por hora",
      label: "Pago por hora (1h)",
      hours: 1,
      factor: 1,
      type: "hora",
    },
    {
      value: "2h × pago por hora",
      label: "Pago por hora (2h)",
      hours: 2,
      factor: 1,
      type: "hora",
    },
    {
      value: "3h × pago por hora",
      label: "Pago por hora (3h)",
      hours: 3,
      factor: 1,
      type: "hora",
    },
    {
      value: "4h × pago por hora",
      label: "Pago por hora (4h)",
      hours: 4,
      factor: 1,
      type: "hora",
    },
    {
      value: "5h × pago por hora",
      label: "Pago por hora (5h)",
      hours: 5,
      factor: 1,
      type: "hora",
    },
    {
      value: "6h × pago por hora",
      label: "Pago por hora (6h)",
      hours: 6,
      factor: 1,
      type: "hora",
    },
    {
      value: "7h × pago por hora",
      label: "Pago por hora (7h)",
      hours: 7,
      factor: 1,
      type: "hora",
    },
    {
      value: "8h × pago por hora",
      label: "Pago por hora (8h)",
      hours: 8,
      factor: 1,
      type: "hora",
    },
    {
      value: `${(fullDayHours + 1).toFixed(1)}h (jornada completa + 1h extras)`,
      label: "Jornada + 1h extra",
      hours: fullDayHours + 1,
      factor: 1,
      type: "extra",
      extraHours: 1,
    },
    {
      value: `${(fullDayHours + 2).toFixed(1)}h (jornada completa + 2h extras)`,
      label: "Jornada + 2h extras",
      hours: fullDayHours + 2,
      factor: 1,
      type: "extra",
      extraHours: 2,
    },
    {
      value: `${(fullDayHours + 3).toFixed(1)}h (jornada completa + 3h extras)`,
      label: "Jornada + 3h extras",
      hours: fullDayHours + 3,
      factor: 1,
      type: "extra",
      extraHours: 3,
    },
    {
      value: `${(fullDayHours + 4).toFixed(1)}h (jornada completa + 4h extras)`,
      label: "Jornada + 4h extras",
      hours: fullDayHours + 4,
      factor: 1,
      type: "extra",
      extraHours: 4,
    },
    {
      value: `${(fullDayHours + 5).toFixed(1)}h (jornada completa + 5h extras)`,
      label: "Jornada + 5h extras",
      hours: fullDayHours + 5,
      factor: 1,
      type: "extra",
      extraHours: 5,
    },
    {
      value: `${(fullDayHours + 6).toFixed(1)}h (jornada completa + 6h extras)`,
      label: "Jornada + 6h extras",
      hours: fullDayHours + 6,
      factor: 1,
      type: "extra",
      extraHours: 6,
    },
    {
      value: `${(fullDayHours + 7).toFixed(1)}h (jornada completa + 7h extras)`,
      label: "Jornada + 7h extras",
      hours: fullDayHours + 7,
      factor: 1,
      type: "extra",
      extraHours: 7,
    },
    {
      value: `${(fullDayHours + 8).toFixed(1)}h (jornada completa + 8h extras)`,
      label: "Jornada + 8h extras",
      hours: fullDayHours + 8,
      factor: 1,
      type: "extra",
      extraHours: 8,
    },
    {
      value: `${(fullDayHours * 2).toFixed(0)}h (doble jornada)`,
      label: `Turno doble (${(fullDayHours * 2).toFixed(0)}h)`,
      hours: fullDayHours * 2,
      factor: 2,
      type: "doble",
    },
    {
      value: `${fullDayHours}h (día de descanso trabajado)`,
      label: "Día descanso trabajado",
      hours: fullDayHours,
      factor: 1 + FREE_DAY_WORKED_PREMIUM_RATE,
      type: "descanso",
    },
    {
      value: "Día de descanso disfrutado",
      label: "Día descanso disfrutado",
      hours: 0,
      factor: 1,
      type: "descanso_disfrutado",
    },
    {
      value: "Falta",
      label: "Falta (sin pago)",
      hours: 0,
      factor: 0,
      type: "falta",
    },
    {
      value: "Vacaciones 25%",
      label: "Vacaciones 25%",
      hours: fullDayHours,
      factor: 1.25,
      type: "vacaciones",
    },
    {
      value: "0h × pago por hora",
      label: "Sin pago (0h)",
      hours: 0,
      factor: 0,
      type: "sin_pago",
    },
    {
      value: "__OTRO__",
      label: "Otro (personalizado)",
      hours: 0,
      factor: 0,
      type: "otro",
    },
  ], [fullDayHours]);

  const calculatePaymentFromConcept = (conceptValue: string): number => {
    const lower = conceptValue.toLowerCase();
    if (
      lower.includes("festivo trabajado") ||
      (lower.includes("día festivo") && lower.includes("trabaj"))
    ) {
      return dailySalaryNum * 1.1;
    }

    const option = conceptOptions.find((opt) => opt.value === conceptValue);
    if (option) {
      switch (option.type) {
        case "jornada":
          return dailySalaryNum;
        case "hora":
          return option.hours * hourlyPayNum;
        case "extra":
          return dailySalaryNum + (option.extraHours || 0) * hourlyPayNum;
        case "doble":
          return calculateDoubleShiftPay(dailySalaryNum);
        case "descanso":
          return dailySalaryNum * option.factor;
        case "descanso_disfrutado":
          return dailySalaryNum;
        case "falta":
          return 0;
        case "vacaciones":
          return calculateVacationPay(dailySalaryNum);
        case "sin_pago":
          return 0;
        default:
          return 0;
      }
    }

    if (lower.includes("doble jornada")) {
      return calculateDoubleShiftPay(dailySalaryNum);
    }
    if (lower.includes("vacaciones")) {
      return calculateVacationPay(dailySalaryNum);
    }
    if (lower.includes("jornada completa")) {
      const extrasMatch = lower.match(/(\d+(?:\.\d+)?)\s*h?\s*extras/i);
      const extraHrs = extrasMatch ? parseFloat(extrasMatch[1]) : 0;
      return dailySalaryNum + extraHrs * hourlyPayNum;
    }
    const hourlyMatch = lower.match(/^(\d+(?:\.\d+)?)h\s*×\s*pago por hora/);
    if (hourlyMatch) {
      return parseFloat(hourlyMatch[1]) * hourlyPayNum;
    }
    return 0;
  };

  const [NormalCalculationNomina, setNormalCalculationNomina] = React.useState(
    data.NormalCalculationNomina || false
  );

  React.useEffect(() => {
    setNormalCalculationNomina(data.NormalCalculationNomina || false);
  }, [data.NormalCalculationNomina]);

  const handleNormalCalculationNominaChange = (checked: boolean) => {
    setNormalCalculationNomina(checked);
    dispatch(
      updateRecordByIndex({
        index: data.index,
        data: {
          ...data,
          NormalCalculationNomina: checked,
        },
      })
    );
  };

  const hourlyPayNumber =
    Number(data.payPerHour.replace(/[^0-9.-]+/g, "")) || 0;

  const calculateRealExtraHours = React.useMemo(() => {
    if (!dates) return 0;

    const startDateUTC = moment
      .utc(dates.start.format("YYYY-MM-DD"))
      .startOf("day");
    const endDateUTC = moment.utc(dates.end.format("YYYY-MM-DD")).endOf("day");

    if (effectiveRealCheckRecords && effectiveRealCheckRecords.length > 0) {
      const filteredRealCheckRecords = effectiveRealCheckRecords.filter(
        (record) => {
          if (!record.assistanceCheck_checkDate) return false;
          const recordDateUTC = moment
            .utc(record.assistanceCheck_checkDate)
            .startOf("day");
          return (
            recordDateUTC.isSameOrAfter(startDateUTC, "day") &&
            recordDateUTC.isSameOrBefore(endDateUTC, "day")
          );
        }
      );

      const formattedData = formatDataForReport(
        filteredRealCheckRecords,
        dates.start.format("YYYY-MM-DD"),
        dates.end.format("YYYY-MM-DD"),
        payrollBranchMeta
      );

      const partnerData = formattedData.filter(
        (checkRecord) => checkRecord.checkIn?.partner_id === data.partnerId
      );

      let totalExtraHours = 0;

      for (const checkRecord of partnerData) {
        if (checkRecord.checkIn && checkRecord.checkOut) {
          const schedule = effectiveRealSchedule.find((s) => {
            const scheduleDay = moment(s.exactDate || s.day).format(
              "YYYY-MM-DD"
            );
            const checkDay = moment
              .utc(checkRecord.checkIn.assistanceCheck_checkDate)
              .format("YYYY-MM-DD");
            return scheduleDay === checkDay;
          });

          if (schedule && schedule.start && schedule.end) {
            const entradaProgramada = schedule.start + ":00";
            const salidaProgramada = schedule.end + ":00";
            const entradaRegistrada =
              checkRecord.checkIn.assistanceCheck_checkTime;
            const salidaRegistrada =
              checkRecord.checkOut.assistanceCheck_checkTime;

            const horasRegistradas = calcularTiempoTrabajado(
              entradaRegistrada,
              salidaRegistrada
            );

            let horasTrabajadasEnHorario = 0;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  horasTrabajadasEnHorario = calcularTiempoTrabajado(
                    entradaRegistrada,
                    "24:00:00"
                  );
                } else if (entradaRegistrada < entradaProgramada) {
                  horasTrabajadasEnHorario = calcularTiempoTrabajado(
                    entradaProgramada,
                    "24:00:00"
                  );
                }
              }
            } else {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  horasTrabajadasEnHorario = calcularTiempoTrabajado(
                    entradaRegistrada,
                    salidaProgramada
                  );
                } else if (
                  entradaRegistrada < entradaProgramada &&
                  salidaRegistrada > entradaProgramada
                ) {
                  horasTrabajadasEnHorario = calcularTiempoTrabajado(
                    entradaProgramada,
                    salidaProgramada
                  );
                }
              }
            }

            let diferencia = horasRegistradas - horasTrabajadasEnHorario;

            if (effectiveSchedules && effectiveSchedules.length > 0) {
              const scheduleDate = moment
                .utc(checkRecord.checkIn.assistanceCheck_checkDate)
                .format("YYYY-MM-DD");
              const scheduleWithIncidents = effectiveSchedules.find(
                (s) =>
                  moment(s.schedule_exactDate).format("YYYY-MM-DD") ===
                  scheduleDate
              );

              if (
                scheduleWithIncidents?.incidents &&
                scheduleHasRejectedExtraPayIncident(
                  scheduleWithIncidents.incidents
                )
              ) {
                diferencia = 0;
              }
            }

            if (diferencia > 0) {
              totalExtraHours += Math.floor(diferencia);
            }
          }
        }
      }

      return totalExtraHours;
    }

    if (detailedRecords && detailedRecords.length > 0) {
      const partnerRecords = getPartnerHoursByDate(
        detailedRecords,
        data.partnerId
      ).filter((record) => {
        const recordDateUTC = moment.utc(record.date).startOf("day");
        return (
          recordDateUTC.isSameOrAfter(startDateUTC, "day") &&
          recordDateUTC.isSameOrBefore(endDateUTC, "day")
        );
      });

      let totalExtraHours = 0;

      for (const record of partnerRecords) {
        const schedule = effectiveRealSchedule.find((s) => {
          const scheduleDay = moment(s.exactDate || s.day).format("YYYY-MM-DD");
          return scheduleDay === record.date;
        });

        if (schedule && schedule.start && schedule.end) {
          const entradaProgramada = schedule.start + ":00";
          const salidaProgramada = schedule.end + ":00";
          const entradaRegistrada = record.checkIn;
          const salidaRegistrada = record.checkOut;

          const scheduledStart = entradaProgramada;
          const scheduledEnd = salidaProgramada;
          const actualStart = entradaRegistrada;
          const actualEnd = salidaRegistrada;

          let horasTrabajadasEnHorario = 0;

          if (scheduledEnd === "00:00:00") {
            if (actualStart !== actualEnd) {
              if (actualStart >= scheduledStart) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  actualStart,
                  "24:00:00"
                );
              } else if (actualStart < scheduledStart) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  scheduledStart,
                  "24:00:00"
                );
              }
            }
          } else {
            if (actualStart !== actualEnd) {
              if (actualStart >= scheduledStart) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  actualStart,
                  scheduledEnd
                );
              } else if (
                actualStart < scheduledStart &&
                actualEnd > scheduledStart
              ) {
                horasTrabajadasEnHorario = calcularTiempoTrabajado(
                  scheduledStart,
                  scheduledEnd
                );
              }
            }
          }

          const totalHoursWorked = record.hoursWorked;
          let diferencia = totalHoursWorked - horasTrabajadasEnHorario;

          if (effectiveSchedules && effectiveSchedules.length > 0) {
            const scheduleDate = record.date;
            const scheduleWithIncidents = effectiveSchedules.find(
              (s) =>
                moment(s.schedule_exactDate).format("YYYY-MM-DD") ===
                scheduleDate
            );

            if (
              scheduleWithIncidents?.incidents &&
              scheduleHasRejectedExtraPayIncident(
                scheduleWithIncidents.incidents
              )
            ) {
              diferencia = 0;
            }
          }

          if (diferencia > 0) {
            totalExtraHours += Math.floor(diferencia);
          }
        }
      }

      return totalExtraHours;
    }

    return 0;
  }, [
    effectiveRealCheckRecords,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    data.partnerId,
    effectiveSchedules,
    detailedRecords,
    effectiveRealSchedule,
  ]);

  const getAppliedBonuses = () => appliedBonuses;

  const getAppliedMissingBoxes = () => appliedMissingBoxes;

  const getAppliedNewDeductions = () => appliedNewDeductions;

  const hasUniformDeduction =
    Boolean(data.uniformDeduction) &&
    Number(String(data.uniformDeduction ?? "").replace(/[^0-9.-]+/g, "")) > 0;

  const paymentDetailEmptyTabStyle: React.CSSProperties = {
    padding: "32px 20px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "0.85rem",
    lineHeight: 1.5,
  };

  const getCelebrityDaysWorked = () => {
    // Usar solo dailyPayments del backend como fuente confiable
    // para evitar duplicados y fechas incorrectas
    const allRecords: any[] = [];
    
    // Solo usar dailyPayments que vienen del backend con la información completa y correcta
    if (fetchedData?.dailyPayments && fetchedData.dailyPayments.length > 0) {
      fetchedData.dailyPayments.forEach((dp: any) => {
        if (dp.isCelebrityDayWorked && dp.celebrityDay) {
          allRecords.push({
            ...dp,
            date: dp.date, // Ya viene en formato YYYY-MM-DD del backend
          });
        }
      });
    }

    // Si no hay dailyPayments, intentar con schedules como fallback
    if (allRecords.length === 0 && effectiveSchedules && effectiveSchedules.length > 0) {
      effectiveSchedules.forEach((s: any) => {
        if (s.isCelebrityDayWorked && s.celebrityDay) {
          const scheduleDate = s.schedule_exactDate || s.exactDate;
          if (scheduleDate) {
            allRecords.push({
              ...s,
              date: scheduleDate,
            });
          }
        }
      });
    }

    if (allRecords.length === 0) {
      return [];
    }

    const celebrityDaysMap = new Map<string, any>();

    allRecords.forEach((record: any) => {
      // Normalizar la fecha usando moment para evitar problemas de zona horaria
      let dateKey: string;
      let normalizedDate: moment.Moment | null = null;
      
      if (record.date) {
        // Intentar parsear con diferentes formatos
        normalizedDate = moment(record.date, ["YYYY-MM-DD", "DD-MM-YYYY", "YYYY/MM/DD", "DD/MM/YYYY"], true);
        if (!normalizedDate.isValid()) {
          // Si no funciona, intentar parsear como ISO string
          normalizedDate = moment(record.date);
        }
      } else {
        const scheduleDate = record.schedule_exactDate || record.exactDate;
        if (scheduleDate) {
          normalizedDate = moment(scheduleDate);
        }
      }

      if (!normalizedDate || !normalizedDate.isValid()) {
        return; // Saltar si no se puede parsear la fecha
      }

      // Usar formato YYYY-MM-DD como clave para evitar duplicados por formato
      const dateKeyISO = normalizedDate.format("YYYY-MM-DD");
      // Formato de visualización DD/MM/YYYY
      dateKey = normalizedDate.format("DD/MM/YYYY");

      // Solo agregar si no existe ya (usando la clave ISO para evitar duplicados)
      if (!celebrityDaysMap.has(dateKeyISO)) {
        // Para nuevos bonos, usar el monto directamente. Para legacy, calcular
        const isNewSystem = record.celebrityDay?.isNewSystem || false;
        const bonusType = isNewSystem 
          ? record.celebrityDay.bonusType || `$${Number(record.celebrityDay.amount || 0).toFixed(2)}`
          : record.celebrityDay.bonusType;
        
        celebrityDaysMap.set(dateKeyISO, {
          date: dateKey, // Formato de visualización DD/MM/YYYY
          title: record.celebrityDay.title,
          bonusType: bonusType,
          isNewSystem: isNewSystem,
          amount: record.celebrityDay.amount ? Number(record.celebrityDay.amount) : undefined,
        });
      }
    });

    // Ordenar por fecha antes de retornar
    const result = Array.from(celebrityDaysMap.values());
    result.sort((a, b) => {
      const dateA = moment(a.date, "DD/MM/YYYY");
      const dateB = moment(b.date, "DD/MM/YYYY");
      return dateA.valueOf() - dateB.valueOf();
    });

    return result;
  };

  const getCelebrityDaysTotalFromBreakdown = () => {
    if (!effectiveSchedules || effectiveSchedules.length === 0) {
      return 0;
    }

    const dailySalary = Number(data.dailySalary.replace(/[^0-9.-]+/g, ""));
    const hourlyPay = Number(data.payPerHour.replace(/[^0-9.-]+/g, ""));

    let total = 0;
    effectiveSchedules.forEach((schedule) => {
      if (schedule.isCelebrityDayWorked && schedule.celebrityDay) {
        const isNewSystem = schedule.celebrityDay.isNewSystem || false;
        
        if (isNewSystem && schedule.celebrityDay.amount) {
          // Para nuevos bonos, usar el monto directamente
          total += Number(schedule.celebrityDay.amount) || 0;
        } else {
          // Para legacy, calcular basándose en bonusType
          let hoursInScheduleForSchedule = 0;
          if (
            schedule.assistanceCheck_checkTime &&
            schedule.assistanceCheckout_checkTime &&
            schedule.schedule_startTime &&
            schedule.schedule_endTime
          ) {
            const [start] = schedule.schedule_startTime.split(" - ");
            const [, end] = schedule.schedule_endTime.split(" - ");
            const entradaProgramada = start + ":00";
            const salidaProgramada = end + ":00";
            const entradaRegistrada = schedule.assistanceCheck_checkTime;
            const salidaRegistrada = schedule.assistanceCheckout_checkTime;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInScheduleForSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    "24:00:00"
                  );
                } else if (entradaRegistrada < entradaProgramada) {
                  hoursInScheduleForSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    "24:00:00"
                  );
                }
              }
            } else {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInScheduleForSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    salidaProgramada
                  );
                } else if (
                  entradaRegistrada < entradaProgramada &&
                  salidaRegistrada > entradaProgramada
                ) {
                  hoursInScheduleForSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    salidaProgramada
                  );
                }
              }
            }
          }

          const dailyPayment = calculateDailyPayment(
            schedule,
            dailySalary,
            hourlyPay,
            NormalCalculationNomina,
            hoursInScheduleForSchedule,
            fullDayHours
          );
          
          // Para legacy, calcular solo el bono adicional (multiplicador - 1)
          if (schedule.celebrityDay.bonusType) {
            const multiplierMatch = schedule.celebrityDay.bonusType.match(/Salario x(\d+\.?\d*)/);
            if (multiplierMatch) {
              const multiplier = parseFloat(multiplierMatch[1]);
              // Solo sumar el bono adicional, no el salario completo
              const bonusAmount = dailyPayment * (multiplier - 1);
              total += bonusAmount;
            } else {
              total += dailyPayment;
            }
          } else {
            total += dailyPayment;
          }
        }
      }
    });

    return total;
  };

  const calculateRealDailyPayments = React.useMemo(() => {
    return () => {
      const freeDaysDatesSet = new Set(
        (chartDaysData?.freeDaysDates || []).map((date: string) => {
          let normalized = moment(date, "DD-MM-YYYY", true);
          if (!normalized.isValid()) {
            normalized = moment(date, "YYYY-MM-DD", true);
          }
          if (!normalized.isValid()) {
            normalized = moment(date);
          }
          return normalized.isValid() ? normalized.format("YYYY-MM-DD") : date;
        })
      );

      const freeDaysEnjoyedDatesSet = new Set(
        (fetchedData?.freeDaysEnjoyedDates || []).map((date: string) => {
          let normalized = moment(date, "YYYY-MM-DD", true);
          if (!normalized.isValid()) {
            normalized = moment(date, "DD-MM-YYYY", true);
          }
          if (!normalized.isValid()) {
            normalized = moment(date);
          }
          return normalized.isValid() ? normalized.format("YYYY-MM-DD") : date;
        })
      );

      if (fetchedData?.dailyPayments && fetchedData.dailyPayments.length > 0) {
        const dailySalary = Number(
          String(data.dailySalary ?? "").replace(/[^0-9.-]+/g, "")
        );
        const hourlyPay = Number(
          String(data.payPerHour ?? "").replace(/[^0-9.-]+/g, "")
        );

        const reconciledDailyPayments =
          dates && effectiveRealCheckRecords?.length
            ? appendMissingChequeoDaysToDailyPayments(
                fetchedData.dailyPayments,
                effectiveRealCheckRecords,
                data.partnerId,
                dates.start.format("YYYY-MM-DD"),
                dates.end.format("YYYY-MM-DD"),
                payrollBranchMeta
              )
            : fetchedData.dailyPayments;

        return mergeDailyPaymentsByDate(reconciledDailyPayments).map((dp: any) => {
          const dpNormalCalculationNomina = NormalCalculationNomina;

          const dayDateNormalized = moment(dp.date, [
            "YYYY-MM-DD",
            "DD-MM-YYYY",
          ]).isValid()
            ? moment(dp.date, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("YYYY-MM-DD")
            : moment(dp.date).format("YYYY-MM-DD");

          const scheduleIncidents = mergeIncidentsForPaymentDay(
            dp.incidents || [],
            allSavedIncidents,
            dayDateNormalized
          );
          const { visits: payableVisits, totalHours: totalHoursRaw } =
            effectivePayrollDayVisits(dp, scheduleIncidents);
          const hoursInSchedule = dp.hoursInSchedule || 0;
          const totalHours = subtractRejectedExtraHours(
            totalHoursRaw,
            scheduleIncidents
          );
          const isCelebrityDayWorked = dp.isCelebrityDayWorked || false;
          const celebrityDay = dp.celebrityDay || null;

          const isFreeDayWorked =
            dp.isFreeDayWorked !== undefined
              ? dp.isFreeDayWorked
              : freeDaysDatesSet.has(dayDateNormalized) &&
                totalHoursRaw > 0 &&
                !freeDaysEnjoyedDatesSet.has(dayDateNormalized);

          const hoursForConcept = dpNormalCalculationNomina
            ? totalHours
            : hoursInSchedule > 0
            ? hoursInSchedule
            : totalHours;

          const dailySalary = Number(
            String(data.dailySalary ?? "").replace(/[^0-9.-]+/g, "")
          );
          const hourlyPay = Number(
            String(data.payPerHour ?? "").replace(/[^0-9.-]+/g, "")
          );

          const scheduleForPayment = buildScheduleForPaymentDay(
            {
              ...dp,
              checkIn: payableVisits[0]?.entradaRegistrada ?? dp.checkIn,
              checkOut:
                payableVisits[payableVisits.length - 1]?.salidaRegistrada ??
                dp.checkOut,
              visits: payableVisits,
              visitCount: payableVisits.length || Number(dp.visitCount) || 0,
            },
            scheduleIncidents,
            totalHours,
            { isCelebrityDayWorked, celebrityDay }
          );

          if (dp.hasPaymentOverride === true) {
            const paymentOverride = Number(dp.payment);

            let concept = "";
            if (
              typeof dp.conceptOverride === "string" &&
              dp.conceptOverride.trim() !== ""
            ) {
              concept = dp.conceptOverride.trim();
            } else {
              concept = getDailyPaymentConcept(
                scheduleForPayment,
                dpNormalCalculationNomina,
                hoursInSchedule,
                fullDayHours
              );
              if (isFreeDayWorked && !concept.includes("descanso trabajado")) {
                concept = concept + " - día de descanso trabajado";
              }
            }

            return {
              date: moment(dayDateNormalized, "YYYY-MM-DD").format("DD/MM/YYYY"),
              hours: formatPayrollHoursDecimal(totalHours),
              hoursInSchedule:
                hoursInSchedule > 0
                  ? formatPayrollHoursDecimal(hoursInSchedule)
                  : "0.0",
              hoursForDisplay:
                payableVisits.length > 1
                  ? totalHours
                  : dpNormalCalculationNomina
                  ? totalHours
                  : hoursInSchedule > 0
                  ? hoursInSchedule
                  : totalHours,
              concept,
              payment: paymentOverride,
              checkIn: payableVisits[0]?.entradaRegistrada ?? dp.checkIn ?? "N/A",
              checkOut:
                payableVisits[payableVisits.length - 1]?.salidaRegistrada ??
                dp.checkOut ??
                "N/A",
              isFreeDayWorked,
              isFreeDayEnjoyed: false,
              hasPaymentOverride: true,
              visitCount: payableVisits.length || dp.visitCount,
              visits: payableVisits,
            };
          }

          const hasRejectedIncidents =
            scheduleHasRejectedExtraPayIncident(scheduleIncidents);
          const hasAcceptedExtraIncident = scheduleIncidents.some(
            isAcceptedExtraRelatedIncident
          );
          const hoursUsedForCalc = dpNormalCalculationNomina
            ? totalHours
            : hoursInSchedule > 0
            ? hoursInSchedule
            : totalHours;
          const chartExtraFromSchedule =
            hoursInSchedule > 0
              ? Math.floor(totalHoursRaw - hoursInSchedule)
              : 0;
          const shouldCapToFullDayWithoutExtra =
            !hasRejectedIncidents &&
            !hasAcceptedExtraIncident &&
            payableVisits.length <= 1 &&
            hoursInSchedule > 0 &&
            chartExtraFromSchedule <= 0 &&
            hoursUsedForCalc >= fullDayHours;

          let recalculatedPayment = dp.payment;
          if (hasRejectedIncidents) {
            const hoursToUse = hoursUsedForCalc;
            if (isDoubleShiftRegisteredHours(totalHours, fullDayHours)) {
              recalculatedPayment = calculateDoubleShiftPay(dailySalary);
            } else {
              const normalHours = Math.min(hoursToUse, fullDayHours);
              if (normalHours === fullDayHours) {
                recalculatedPayment = dailySalary;
              } else {
                const displayHours = Math.floor(normalHours);
                recalculatedPayment = displayHours * hourlyPay;
              }
            }
          } else {
            recalculatedPayment = calculateDailyPayment(
              scheduleForPayment,
              dailySalary,
              hourlyPay,
              dpNormalCalculationNomina,
              hoursInSchedule,
              fullDayHours
            );

            if (shouldCapToFullDayWithoutExtra) {
              recalculatedPayment = dailySalary;
            }

            // Add extra pay for accepted "Salió tarde" / "Llegó temprano" incidents.
            // calculateDailyPayment uses hoursInSchedule (or rounds totalHours in a way that
            // drops the extra time beyond the schedule end). We add the missing portion here,
            // matching the chart's formula: Math.floor(totalHours - hoursInSchedule).
            // To avoid double-counting we subtract extra hours already included by calculateDailyPayment.
            if (hoursInSchedule > 0 && hasAcceptedExtraIncident) {
              const chartExtra = Math.floor(totalHoursRaw - hoursInSchedule);
              const alreadyIncluded = Math.max(
                0,
                Math.floor(roundHours(hoursUsedForCalc, fullDayHours) - fullDayHours)
              );
              const additionalExtra = Math.max(0, chartExtra - alreadyIncluded);
              if (additionalExtra > 0) {
                recalculatedPayment += additionalExtra * hourlyPay;
              }
            }
          }

          if (
            isFreeDayWorked &&
            !hasRejectedIncidents &&
            !hasAcceptedFreeDayWorkedIncident(scheduleIncidents)
          ) {
            recalculatedPayment = withFreeDayWorkedPremium(recalculatedPayment);
          }

          let concept = "";

          if (hasRejectedIncidents) {
            const hoursToUse = hoursUsedForCalc;
            if (isDoubleShiftRegisteredHours(totalHours, fullDayHours)) {
              const totalHoursRounded = Math.floor(totalHours);
              concept = `${totalHoursRounded}h (doble jornada) (incidencias rechazadas)`;
            } else {
              const normalHours = Math.min(hoursToUse, fullDayHours);
              if (normalHours === fullDayHours) {
                concept = `${normalHours}h (jornada completa) (incidencias rechazadas)`;
              } else {
                const displayHours = Math.floor(hoursToUse);
                concept = `${displayHours}h × pago por hora (incidencias rechazadas)`;
              }
            }
          } else {
            concept = getDailyPaymentConcept(
              scheduleForPayment,
              dpNormalCalculationNomina,
              hoursInSchedule,
              fullDayHours
            );
            if (shouldCapToFullDayWithoutExtra) {
              concept = `${fullDayHours}h (jornada completa)`;
            }
            if (hoursInSchedule > 0 && hasAcceptedExtraIncident) {
              const chartExtra = Math.floor(totalHoursRaw - hoursInSchedule);
              const alreadyIncluded = Math.max(
                0,
                Math.floor(roundHours(hoursUsedForCalc, fullDayHours) - fullDayHours)
              );
              const additionalExtra = Math.max(0, chartExtra - alreadyIncluded);
              if (additionalExtra > 0) {
                concept = concept + ` + ${additionalExtra}h extras (aceptada)`;
              }
            }
            concept = appendMultiVisitExtrasToConcept(
              concept,
              totalHoursRaw,
              fullDayHours,
              scheduleForPayment,
              dpNormalCalculationNomina
            );
            if (isFreeDayWorked && !concept.includes("descanso trabajado")) {
              concept = concept + " - día de descanso trabajado";
            }
          }

          return {
            date: moment(dayDateNormalized, "YYYY-MM-DD").format("DD/MM/YYYY"),
            hours: formatPayrollHoursDecimal(totalHours),
            hoursInSchedule:
              hoursInSchedule > 0
                ? formatPayrollHoursDecimal(hoursInSchedule)
                : "0.0",
            hoursForDisplay:
              payableVisits.length > 1
                ? totalHours
                : dpNormalCalculationNomina
                ? totalHours
                : hoursInSchedule > 0
                ? hoursInSchedule
                : totalHours,
            concept: concept,
            payment: recalculatedPayment,
            checkIn: payableVisits[0]?.entradaRegistrada ?? dp.checkIn ?? "N/A",
            checkOut:
              payableVisits[payableVisits.length - 1]?.salidaRegistrada ??
              dp.checkOut ??
              "N/A",
            isFreeDayWorked: isFreeDayWorked,
            isFreeDayEnjoyed: false,
            hasPaymentOverride: false,
            visitCount: payableVisits.length || dp.visitCount,
            visits: payableVisits,
          };
        });
      }

      if (
        effectiveRealCheckRecords &&
        effectiveRealCheckRecords.length > 0 &&
        dates
      ) {
        const dailySalary = Number(data.dailySalary.replace(/[^0-9.-]+/g, ""));
        const hourlyPay = Number(data.payPerHour.replace(/[^0-9.-]+/g, ""));

        const formattedData = formatDataForReport(
          effectiveRealCheckRecords,
          dates.start.format("YYYY-MM-DD"),
          dates.end.format("YYYY-MM-DD"),
          payrollBranchMeta
        );

        const partnerData = formattedData.filter(
          (checkRecord) => checkRecord.checkIn?.partner_id === data.partnerId
        );

        const payments = partnerData
          .map((checkRecord) => {
            if (!checkRecord.checkIn || !checkRecord.checkOut) {
              return null;
            }

            const checkInTime = checkRecord.checkIn.assistanceCheck_checkTime;
            const checkOutTime = checkRecord.checkOut.assistanceCheck_checkTime;
            const checkDate = checkRecord.checkIn.assistanceCheck_checkDate;
            const checkDateNormalized = moment
              .utc(checkDate)
              .format("YYYY-MM-DD");
            const checkDateFormatted = moment
              .utc(checkDate)
              .format("DD/MM/YYYY");
            const isFreeDayWorked =
              freeDaysDatesSet.has(checkDateNormalized) &&
              !freeDaysEnjoyedDatesSet.has(checkDateNormalized);

            const totalHours = calcularTiempoTrabajado(
              checkInTime,
              checkOutTime
            );

            const correspondingSchedule = effectiveSchedules.find(
              (schedule) =>
                moment(schedule.schedule_exactDate).format("YYYY-MM-DD") ===
                moment.utc(checkDate).format("YYYY-MM-DD")
            );

            let hoursInSchedule = 0;
            let hasScheduledTime = false;

            const scheduleEntry = effectiveRealSchedule.find((s) => {
              const scheduleDay = moment(s.exactDate || s.day).format(
                "YYYY-MM-DD"
              );
              const checkDay = moment.utc(checkDate).format("YYYY-MM-DD");
              return scheduleDay === checkDay;
            });

            if (scheduleEntry && scheduleEntry.start && scheduleEntry.end) {
              hasScheduledTime = true;
              const entradaProgramada = scheduleEntry.start + ":00";
              const salidaProgramada = scheduleEntry.end + ":00";
              const entradaRegistrada = checkInTime;
              const salidaRegistrada = checkOutTime;

              if (salidaProgramada === "00:00:00") {
                if (entradaRegistrada !== salidaRegistrada) {
                  if (entradaRegistrada >= entradaProgramada) {
                    hoursInSchedule = calcularTiempoTrabajado(
                      entradaRegistrada,
                      "24:00:00"
                    );
                  } else if (entradaRegistrada < entradaProgramada) {
                    hoursInSchedule = calcularTiempoTrabajado(
                      entradaProgramada,
                      "24:00:00"
                    );
                  }
                }
              } else {
                if (entradaRegistrada !== salidaRegistrada) {
                  if (entradaRegistrada >= entradaProgramada) {
                    hoursInSchedule = calcularTiempoTrabajado(
                      entradaRegistrada,
                      salidaProgramada
                    );
                  } else if (
                    entradaRegistrada < entradaProgramada &&
                    salidaRegistrada > entradaProgramada
                  ) {
                    hoursInSchedule = calcularTiempoTrabajado(
                      entradaProgramada,
                      salidaProgramada
                    );
                  }
                }
              }
            }

            let dailyPayment = 0;
            let concept = "";

            if (hasScheduledTime && correspondingSchedule) {
              const hasRejectedIncidents =
                scheduleHasRejectedExtraPayIncident(
                  correspondingSchedule.incidents || []
                );

              if (hasRejectedIncidents) {
                const hoursToUse = NormalCalculationNomina
                  ? totalHours
                  : hoursInSchedule > 0
                  ? hoursInSchedule
                  : totalHours;
                const normalHours = Math.min(hoursToUse, fullDayHours);
                if (normalHours === fullDayHours) {
                  dailyPayment = dailySalary;
                  concept = `${normalHours}h (jornada completa) (incidencias rechazadas)`;
                } else {
                  const displayHours = Math.floor(normalHours);
                  dailyPayment = displayHours * hourlyPay;
                  concept = `${displayHours}h × pago por hora (incidencias rechazadas)`;
                }
              } else {
                const scheduleWithRealTimes = {
                  ...correspondingSchedule,
                  assistanceCheck_checkTime: checkInTime,
                  assistanceCheckout_checkTime: checkOutTime,
                  schedule_startTime: `${scheduleEntry.start} - ${scheduleEntry.end}`,
                  schedule_endTime: `${scheduleEntry.start} - ${scheduleEntry.end}`,
                };

                dailyPayment = calculateDailyPayment(
                  scheduleWithRealTimes,
                  dailySalary,
                  hourlyPay,
                  NormalCalculationNomina,
                  hoursInSchedule,
                  fullDayHours
                );
                concept = getDailyPaymentConcept(
                  scheduleWithRealTimes,
                  NormalCalculationNomina,
                  hoursInSchedule,
                  fullDayHours
                );
              }

              if (
                isFreeDayWorked &&
                totalHours > 0 &&
                !hasRejectedIncidents &&
                !hasAcceptedFreeDayWorkedIncident(
                  correspondingSchedule?.incidents || []
                )
              ) {
                dailyPayment = withFreeDayWorkedPremium(dailyPayment);
                if (!concept.includes("descanso trabajado")) {
                  concept = concept + " - día de descanso trabajado";
                }
              }
            } else {
              const hoursToUse = totalHours;
              const roundedHours = roundHours(hoursToUse, fullDayHours);

              if (roundedHours < fullDayHours) {
                const displayHours =
                  roundedHours % 1 === 0
                    ? roundedHours.toString()
                    : roundedHours.toFixed(1);
                dailyPayment = roundedHours * hourlyPay;
                concept = `${displayHours}h × pago por hora`;
              } else {
                dailyPayment = dailySalary;
                concept = `${fullDayHours}h (jornada completa)`;
              }

              if (
                isFreeDayWorked &&
                totalHours > 0 &&
                !hasAcceptedFreeDayWorkedIncident(
                  correspondingSchedule?.incidents || []
                )
              ) {
                dailyPayment = withFreeDayWorkedPremium(dailyPayment);
                if (!concept.includes("descanso trabajado")) {
                  concept = concept + " - día de descanso trabajado";
                }
              }
            }

            const hoursForDisplay = NormalCalculationNomina
              ? totalHours
              : hoursInSchedule > 0
              ? hoursInSchedule
              : totalHours;

            return {
              date: checkDateFormatted,
              hours: formatPayrollHoursDecimal(totalHours),
              hoursInSchedule:
                hoursInSchedule > 0
                  ? formatPayrollHoursDecimal(hoursInSchedule)
                  : "0.0",
              hoursForDisplay: hoursForDisplay,
              concept: concept,
              payment: dailyPayment,
              checkIn: checkInTime,
              checkOut: checkOutTime,
              isFreeDayWorked: isFreeDayWorked,
              isFreeDayEnjoyed: false,
              hasPaymentOverride: false,
            };
          })
          .filter(Boolean);

        return payments.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      }

      if (effectiveSchedules && effectiveSchedules.length > 0) {
        const dailySalary = Number(data.dailySalary.replace(/[^0-9.-]+/g, ""));
        const hourlyPay = Number(data.payPerHour.replace(/[^0-9.-]+/g, ""));

        const payments = effectiveSchedules.map((schedule) => {
          let dailyPayment = 0;
          let concept = "";

          const scheduleDateNormalized = moment(
            schedule.schedule_exactDate
          ).format("YYYY-MM-DD");
          const scheduleDateFormatted = new Date(
            schedule.schedule_exactDate
          ).toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const isFreeDayWorked =
            freeDaysDatesSet.has(scheduleDateNormalized) &&
            !freeDaysEnjoyedDatesSet.has(scheduleDateNormalized);

          const hasRejectedIncidents = scheduleHasRejectedExtraPayIncident(
            schedule.incidents || []
          );

          let totalHours = 0;
          if (
            schedule.assistanceCheck_checkTime &&
            schedule.assistanceCheckout_checkTime
          ) {
            totalHours = calcularTiempoTrabajado(
              schedule.assistanceCheck_checkTime,
              schedule.assistanceCheckout_checkTime
            );
          }

          let hoursInSchedule = 0;
          if (
            schedule.assistanceCheck_checkTime &&
            schedule.assistanceCheckout_checkTime &&
            schedule.schedule_startTime &&
            schedule.schedule_endTime
          ) {
            const [start] = schedule.schedule_startTime.split(" - ");
            const [, end] = schedule.schedule_endTime.split(" - ");
            const entradaProgramada = start + ":00";
            const salidaProgramada = end + ":00";
            const entradaRegistrada = schedule.assistanceCheck_checkTime;
            const salidaRegistrada = schedule.assistanceCheckout_checkTime;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    "24:00:00"
                  );
                } else if (entradaRegistrada < entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    "24:00:00"
                  );
                }
              }
            } else {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    salidaProgramada
                  );
                } else if (
                  entradaRegistrada < entradaProgramada &&
                  salidaRegistrada > entradaProgramada
                ) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    salidaProgramada
                  );
                }
              }
            }
          }

          if (hasRejectedIncidents) {
            const hoursToUse = NormalCalculationNomina
              ? totalHours
              : hoursInSchedule > 0
              ? hoursInSchedule
              : totalHours;
            const normalHours = Math.min(hoursToUse, fullDayHours);
            if (normalHours === fullDayHours) {
              dailyPayment = dailySalary;
              concept = `${normalHours}h (jornada completa) (incidencias rechazadas)`;
            } else {
              const displayHours = Math.floor(normalHours);
              dailyPayment = displayHours * hourlyPay;
              concept = `${displayHours}h × pago por hora (incidencias rechazadas)`;
            }
          } else {
            dailyPayment = calculateDailyPayment(
              schedule,
              dailySalary,
              hourlyPay,
              NormalCalculationNomina,
              hoursInSchedule,
              fullDayHours
            );
            concept = getDailyPaymentConcept(
              schedule,
              NormalCalculationNomina,
              hoursInSchedule,
              fullDayHours
            );
          }

          if (
            isFreeDayWorked &&
            totalHours > 0 &&
            !hasRejectedIncidents &&
            !hasAcceptedFreeDayWorkedIncident(schedule.incidents || [])
          ) {
            dailyPayment = withFreeDayWorkedPremium(dailyPayment);
            if (!concept.includes("descanso trabajado")) {
              concept = concept + " - día de descanso trabajado";
            }
          }

          const hoursForDisplay = NormalCalculationNomina
            ? totalHours
            : hoursInSchedule > 0
            ? hoursInSchedule
            : totalHours;

          return {
            date: scheduleDateFormatted,
            hours: formatPayrollHoursDecimal(totalHours),
            hoursInSchedule:
              hoursInSchedule > 0
                ? formatPayrollHoursDecimal(hoursInSchedule)
                : "0.0",
            hoursForDisplay: hoursForDisplay,
            concept: concept,
            payment: dailyPayment,
            checkIn: schedule.assistanceCheck_checkTime || "N/A",
            checkOut: schedule.assistanceCheckout_checkTime || "N/A",
            isFreeDayWorked: isFreeDayWorked,
            isFreeDayEnjoyed: false,
            hasPaymentOverride: false,
          };
        });

        return payments.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      } else {
        if (!effectiveRealSchedule || effectiveRealSchedule.length === 0)
          return [];

        const payments = effectiveRealSchedule.map((schedule) => {
          const hoursWorked = Number(schedule.worked);
          const dailySalary = Number(
            data.dailySalary.replace(/[^0-9.-]+/g, "")
          );
          const hourlyPay = Number(data.payPerHour.replace(/[^0-9.-]+/g, ""));

          const scheduleDateNormalized = moment(schedule.day).format(
            "YYYY-MM-DD"
          );
          const scheduleDateFormatted = new Date(
            schedule.day
          ).toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const isFreeDayWorked =
            freeDaysDatesSet.has(scheduleDateNormalized) &&
            !freeDaysEnjoyedDatesSet.has(scheduleDateNormalized);

          let hoursInSchedule = 0;
          if (schedule.checkIn?.checkTime && schedule.checkOut?.checkTime) {
            const entradaProgramada = schedule.start + ":00";
            const salidaProgramada = schedule.end + ":00";
            const entradaRegistrada = schedule.checkIn.checkTime;
            const salidaRegistrada = schedule.checkOut.checkTime;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    "24:00:00"
                  );
                } else if (entradaRegistrada < entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    "24:00:00"
                  );
                }
              }
            } else {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    salidaProgramada
                  );
                } else if (
                  entradaRegistrada < entradaProgramada &&
                  salidaRegistrada > entradaProgramada
                ) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    salidaProgramada
                  );
                }
              }
            }
          }

          const hoursToUse = NormalCalculationNomina
            ? hoursWorked
            : hoursInSchedule > 0
            ? hoursInSchedule
            : hoursWorked;
          const roundedHours = roundHours(hoursToUse, fullDayHours);

          let dailyPayment = 0;
          let concept = "";

          if (roundedHours < fullDayHours) {
            const displayHours =
              roundedHours % 1 === 0
                ? roundedHours.toString()
                : roundedHours.toFixed(1);
            dailyPayment = roundedHours * hourlyPay;
            concept = `${displayHours}h × pago por hora`;
          } else if (Math.abs(roundedHours - fullDayHours) < 0.01) {
            dailyPayment = dailySalary;
            concept = `${fullDayHours}h (jornada completa)`;
          } else if (
            Math.abs(roundedHours - (fullDayHours + 0.5)) < 0.01 ||
            roundedHours === fullDayHours + 0.5
          ) {
            dailyPayment = dailySalary;
            concept = `${fullDayHours}h (jornada completa)`;
          } else if (Math.abs(roundedHours - (fullDayHours + 1)) < 0.01) {
            dailyPayment = dailySalary + 1 * hourlyPay;
            concept = `${(fullDayHours + 1).toFixed(1)}h (jornada completa + 1h extras)`;
          } else {
            const extraHours = Math.floor(roundedHours - fullDayHours);
            const displayHours = roundedHours.toFixed(1);
            dailyPayment = dailySalary + extraHours * hourlyPay;
            concept = `${displayHours}h (jornada completa + ${extraHours}h extras)`;
          }

          if (
            isFreeDayWorked &&
            hoursWorked > 0 &&
            !hasAcceptedFreeDayWorkedIncident(
              (schedule as { incidents?: any[] }).incidents || []
            )
          ) {
            dailyPayment = withFreeDayWorkedPremium(dailyPayment);
            if (!concept.includes("descanso trabajado")) {
              concept = concept + " - día de descanso trabajado";
            }
          }

          const hoursForDisplay = NormalCalculationNomina
            ? hoursWorked
            : hoursInSchedule > 0
            ? hoursInSchedule
            : hoursWorked;

          return {
            date: scheduleDateFormatted,
            hours: formatPayrollHoursDecimal(hoursWorked),
            hoursInSchedule:
              hoursInSchedule > 0
                ? formatPayrollHoursDecimal(hoursInSchedule)
                : "0.0",
            hoursForDisplay: hoursForDisplay,
            concept: concept,
            payment: dailyPayment,
            checkIn: schedule.checkIn?.checkTime || "N/A",
            checkOut: schedule.checkOut?.checkTime || "N/A",
            isFreeDayWorked: isFreeDayWorked,
            isFreeDayEnjoyed: false,
            hasPaymentOverride: false,
          };
        });

        return payments.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      }
    };
  }, [
    fetchedData?.dailyPayments,
    fetchedData?.schedules,
    fetchedData?.freeDaysEnjoyedDates,
    effectiveRealCheckRecords,
    effectiveSchedules,
    effectiveRealSchedule,
    dates?.start?.format("YYYY-MM-DD"),
    dates?.end?.format("YYYY-MM-DD"),
    data.partnerId,
    data.dailySalary,
    data.payPerHour,
    NormalCalculationNomina,
    detailedRecords,
    chartDaysData?.freeDaysDates,
    allSavedIncidents,
    fullDayHours,
    payrollBranchMeta,
  ]);

  const dailyPayments = (calculateRealDailyPayments() || []).filter(Boolean) as any[];

  const extraHoursChart = React.useMemo(() => {
    if (dailyPayments.length > 0) {
      return sumExtraHoursFromDailyPayments(dailyPayments, fullDayHours);
    }
    if ((chartDaysData?.detailed?.length ?? 0) > 0) {
      return summarizeChartDays(
        chartDaysData?.detailed || [],
        fullDayHours
      ).extraHours;
    }
    return calculateRealExtraHours;
  }, [
    dailyPayments,
    chartDaysData?.detailed,
    fullDayHours,
    calculateRealExtraHours,
  ]);
  const extraHourPayTotalNumber = extraHoursChart * hourlyPayNumber;
  const extraHourPayTotalCurrency = getCurrencyFormat(extraHourPayTotalNumber);

  const resolvedAbsenceRows = React.useMemo(() => {
    if ((fetchedData?.absenceRows || []).length > 0) {
      return fetchedData!.absenceRows!;
    }
    // Fallback: chart-days-worked ya incluye faltas; el preload no siempre traía absenceRows.
    return (chartDaysData?.detailed || [])
      .filter((row: any) => row?.isAbsence === true)
      .map((row: any) => {
        const normalizedDate = moment(
          row.fecha,
          ["DD-MM-YYYY", "DD/MM/YYYY"],
          true
        ).isValid()
          ? moment(row.fecha, ["DD-MM-YYYY", "DD/MM/YYYY"]).format("YYYY-MM-DD")
          : moment(row.fecha).format("YYYY-MM-DD");
        return {
          normalizedDate,
          date: moment(normalizedDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
          entradaProgramada: row.entradaProgramada || "N/A",
          salidaProgramada: row.salidaProgramada || "N/A",
        };
      });
  }, [fetchedData?.absenceRows, chartDaysData?.detailed]);

  const allDailyPayments = React.useMemo(() => {
    const payments = [...dailyPayments];
    const freeDaysEnjoyed = fetchedData?.freeDaysEnjoyedDates || [];
    const getPaymentSortValue = (row: any) => {
      const normalizedDate = row?.normalizedDate;
      if (normalizedDate) {
        return moment(normalizedDate, "YYYY-MM-DD").valueOf();
      }

      return moment(row?.date, [
        "DD/MM/YYYY",
        "DD-MM-YYYY",
        "YYYY-MM-DD",
      ]).valueOf();
    };

    freeDaysEnjoyed.forEach((freeDayDate: string) => {
      const existsInPayments = payments.some((dp) => {
        const dpDate = moment(dp.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).format(
          "DD/MM/YYYY"
        );
        const freeDayFormatted = moment(freeDayDate, "YYYY-MM-DD").format(
          "DD/MM/YYYY"
        );
        return dpDate === freeDayFormatted;
      });

      if (!existsInPayments) {
        const dailySalary =
          Number(data.dailySalary.replace(/[^0-9.-]+/g, "")) || 0;
        payments.push({
          date: moment(freeDayDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
          hours: "0.00",
          hoursInSchedule: "0.00",
          hoursForDisplay: 0,
          concept: "Día de descanso disfrutado",
          payment: dailySalary,
          checkIn: "N/A",
          checkOut: "N/A",
          isFreeDayWorked: false,
          isFreeDayEnjoyed: true,
          hasPaymentOverride: false,
        });
      }
    });

    const sortedPayments = payments.sort((a, b) => {
      return getPaymentSortValue(a) - getPaymentSortValue(b);
    });

    const incapacityDateSet = new Set(
      (incapacityRows || []).map((row) => row.normalizedDate)
    );
    const permissionDateSet = new Set(permissionDates || []);
    const absenceDateSet = new Set(
      resolvedAbsenceRows
        .map((row: any) => String(row.normalizedDate || "").trim())
        .filter(Boolean)
    );

    const paymentsWithIncapacityFlag = sortedPayments.map((payment: any) => {
      const normalizedDate = moment(payment.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).format(
        "YYYY-MM-DD"
      );
      return {
        ...payment,
        isAbsence: payment.isAbsence === true || absenceDateSet.has(normalizedDate),
        isIncapacity: incapacityDateSet.has(normalizedDate),
        isPermission: permissionDateSet.has(normalizedDate),
      };
    });

    const existingPaymentDates = new Set(
      paymentsWithIncapacityFlag.map((payment: any) =>
        moment(payment.date, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD")
      )
    );
    const extraIncapacityRows = (incapacityRows || [])
      .filter((row) => !existingPaymentDates.has(row.normalizedDate))
      .map((row) => ({
        ...row,
        isPermission: permissionDateSet.has(row.normalizedDate),
      }));

    const extraPermissionRows = (permissionDates || [])
      .filter(
        (normalizedDate) =>
          !existingPaymentDates.has(normalizedDate) &&
          !incapacityDateSet.has(normalizedDate)
      )
      .map((normalizedDate) => ({
        date: moment(normalizedDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
        normalizedDate,
        hours: "0.00",
        hoursInSchedule: "0.00",
        hoursForDisplay: 0,
        concept: "Permiso",
        payment: 0,
        checkIn: "N/A",
        checkOut: "N/A",
        isFreeDayWorked: false,
        isFreeDayEnjoyed: false,
        isPermission: true,
        hasPaymentOverride: false,
      }));

    const absenceRows = resolvedAbsenceRows
      .filter((row: any) => {
        const normalizedDate = String(row.normalizedDate || "").trim();
        return (
          normalizedDate &&
          !existingPaymentDates.has(normalizedDate) &&
          !incapacityDateSet.has(normalizedDate) &&
          !permissionDateSet.has(normalizedDate)
        );
      })
      .map((row: any) => ({
        date: moment(row.normalizedDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
        normalizedDate: row.normalizedDate,
        hours: "0.00",
        hoursInSchedule: "0.00",
        hoursForDisplay: 0,
        concept: "Falta",
        payment: 0,
        checkIn: "N/A",
        checkOut: "N/A",
        entradaProgramada: row.entradaProgramada || "N/A",
        salidaProgramada: row.salidaProgramada || "N/A",
        isFreeDayWorked: false,
        isFreeDayEnjoyed: false,
        isAbsence: true,
        hasPaymentOverride: false,
      }));

    // Se agregan solo los días que aún no existen en el desglose.
    return [
      ...paymentsWithIncapacityFlag,
      ...extraIncapacityRows,
      ...extraPermissionRows,
      ...absenceRows,
    ].sort((a: any, b: any) => {
      return getPaymentSortValue(a) - getPaymentSortValue(b);
    });
  }, [
    dailyPayments,
    fetchedData?.freeDaysEnjoyedDates,
    resolvedAbsenceRows,
    data.dailySalary,
    incapacityRows,
    permissionDates,
  ]);

  const totalDailyPayments = allDailyPayments.reduce(
    (sum, day) => sum + day.payment,
    0
  );

  const calculatedFreeDays = React.useMemo(() => {
    const freeDaysDatesSet = new Set(
      (chartDaysData?.freeDaysDates || []).map((date: string) => {
        let normalized = moment(date, "DD-MM-YYYY", true);
        if (!normalized.isValid()) {
          normalized = moment(date, "YYYY-MM-DD", true);
        }
        if (!normalized.isValid()) {
          normalized = moment(date);
        }
        return normalized.isValid() ? normalized.format("YYYY-MM-DD") : date;
      })
    );

    const freeDaysEnjoyedDatesSet = new Set(
      (fetchedData?.freeDaysEnjoyedDates || []).map((date: string) => {
        let normalized = moment(date, "YYYY-MM-DD", true);
        if (!normalized.isValid()) {
          normalized = moment(date, "DD-MM-YYYY", true);
        }
        if (!normalized.isValid()) {
          normalized = moment(date);
        }
        return normalized.isValid() ? normalized.format("YYYY-MM-DD") : date;
      })
    );

    const freeDaysEnjoyedCount = allDailyPayments.filter((day: any) =>
      isReciboRestDayEnjoyedRow(day)
    ).length;

    const freeDaysWorkedCount = allDailyPayments.filter((day: any) =>
      isReciboRestDayWorkedRow(day)
    ).length;

    return {
      freeDaysWorked: freeDaysWorkedCount,
      freeDaysEnjoyed: freeDaysEnjoyedCount,
    };
  }, [
    allDailyPayments,
    dailyPayments,
    chartDaysData?.freeDaysDates,
    fetchedData?.freeDaysEnjoyedDates,
  ]);

  const freeDaysEnjoyedPayAmount = React.useMemo(() => {
    const dailySalary =
      Number(String(data.dailySalary ?? "").replace(/[^0-9.-]+/g, "")) || 0;
    return calculatedFreeDays.freeDaysEnjoyed * dailySalary;
  }, [calculatedFreeDays.freeDaysEnjoyed, data.dailySalary]);

  const freeDaysWorkedPayAmount = React.useMemo(() => {
    let hoursFreeDaysWorked = 0;
    let totalPayAmount = 0;

    allDailyPayments.forEach((day: any) => {
      if (isReciboRestDayWorkedRow(day)) {
        const dayHours = parseFloat(day.hours || "0");
        const dayPayment =
          typeof day.payment === "number"
            ? day.payment
            : parseFloat(String(day.payment || 0).replace(/[^0-9.-]+/g, ""));
        hoursFreeDaysWorked += dayHours;
        totalPayAmount += dayPayment;
      }
    });

    return {
      hours: hoursFreeDaysWorked,
      payAmount: totalPayAmount,
    };
  }, [allDailyPayments]);

  // Método que calcula totalPayWithIncentives igual que en el backend
  // Cálculo exacto igual que en el backend (línea 2582-2585 de payroll.service.ts)
  // totalPayWithIncentives = totalDailyPayments + totalIncentives - (totalDeductions + totalMissingBoxDeductions + uniformDeduction)
  const calculateTotalPayWithIncentives = React.useMemo(() => {
    const totalDailyPaymentsNumber = totalDailyPayments;
    const totalIncentivesNumber = resolvedBonusDeductionTotals.totalIncentivesNumber;
    const totalDeductionsNumber = resolvedBonusDeductionTotals.catalogDeductions;
    const totalMissingBoxDeductions =
      resolvedBonusDeductionTotals.totalMissingBoxDeductions;
    const uniformDeductionAmount = resolvedBonusDeductionTotals.uniformDeduction;

    const totalPayWithIncentivesNumber =
      totalDailyPaymentsNumber +
      totalIncentivesNumber -
      (totalDeductionsNumber + totalMissingBoxDeductions + uniformDeductionAmount);

    return {
      totalDailyPayments: totalDailyPaymentsNumber,
      totalIncentives: totalIncentivesNumber,
      totalDeductions: totalDeductionsNumber,
      totalMissingBoxDeductions,
      uniformDeduction: uniformDeductionAmount,
      totalPayWithIncentives: totalPayWithIncentivesNumber,
      totalPayWithIncentivesFormatted: getCurrencyFormat(
        totalPayWithIncentivesNumber
      ),
    };
  }, [totalDailyPayments, resolvedBonusDeductionTotals]);

  // Mantener el Pago estimado del cuadro alineado con el desglose.
  React.useEffect(() => {
    if (data.index === undefined) return;
    const totalNum = calculateTotalPayWithIncentives.totalPayWithIncentives;
    if (!Number.isFinite(totalNum)) return;
    const currentNum =
      Number(String(data.totalPayWithIncentives ?? "").replace(/[^0-9.-]+/g, "")) ||
      0;
    if (Math.abs(currentNum - totalNum) < 0.015) return;
    dispatch(
      updateRecordByIndex({
        index: data.index,
        // Solo el campo del total: no reenviar el record completo para
        // evitar cascadas que remontan el modal de desglose.
        data: {
          totalPayWithIncentives: getCurrencyFormat(totalNum),
        } as IPayrollReport,
      })
    );
  }, [
    calculateTotalPayWithIncentives.totalPayWithIncentives,
    data.index,
    data.totalPayWithIncentives,
    dispatch,
  ]);

  const paymentCorrected = React.useMemo(() => {
    return {
      ...payment,
      freeDaysWorked: calculatedFreeDays.freeDaysWorked,
      freeDaysEnjoyed: calculatedFreeDays.freeDaysEnjoyed,
      hoursFreeDaysWorked: freeDaysWorkedPayAmount.hours,
      hoursAuthorizedPay: getCurrencyFormat(totalDailyPayments),
      extraHourPayTotal: getCurrencyFormat(extraHourPayTotalNumber),
      doubleShiftPayTotal: getCurrencyFormat(
        parsePayrollMoneyValue(payment.doubleShiftPayTotal)
      ),
      freeDayWorkedPayTotal: getCurrencyFormat(
        freeDaysWorkedPayAmount.payAmount
      ),
      freeDaysEnjoyedPay: getCurrencyFormat(freeDaysEnjoyedPayAmount),
      totalIncentives: payment.totalIncentives,
      totalDeductions: payment.totalDeductions,
    };
  }, [
    totalDailyPayments,
    extraHourPayTotalNumber,
    payment,
    NormalCalculationNomina,
    calculatedFreeDays,
    freeDaysEnjoyedPayAmount,
    freeDaysWorkedPayAmount,
  ]);

  const conceptos = getConceptos(getCelebrityDaysTotalFromBreakdown, fullDayHours).map((c) =>
    c.key === "extraHourPayTotal"
      ? {
          ...c,
          value: (_payment: any, _data: any) => {
            const integerHours = Math.floor(extraHoursChart);
            return integerHours > 0
              ? `${integerHours} (${formatHoursToTime(extraHoursChart)}h)`
              : "0";
          },
          money: () => extraHourPayTotalCurrency,
        }
      : c
  );

  /**
   * Exporta los datos del detalle de pago a un archivo Excel en formato de recibo de nómina
   */
  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    let startDateDisplay = "N/A";
    let endDateDisplay = "N/A";
    let paymentDate = "N/A";
    if (dates?.start && dates?.end) {
      const startDate = dates.start.clone().add(3, "days");
      const endDate = dates.end.clone().add(3, "days");
      startDateDisplay = startDate.format("DD-MM-YYYY");
      endDateDisplay = endDate.format("DD-MM-YYYY");

      const startDay = startDate.date();
      const endDay = endDate.date();

      if (startDay <= 15 && endDay <= 15) {
        paymentDate = "15";
      } else {
        const lastDayOfMonth = endDate.clone().endOf("month").date();
        paymentDate = lastDayOfMonth.toString();
      }
    }

    const reciboData = [];

    reciboData.push(["RECIBO DE NÓMINA", "", "", "", ""]);
    reciboData.push([]);

    reciboData.push(["NOMBRE DEL EMPLEADO:", data.name || "", "", "", ""]);
    reciboData.push(["SUELDO BASE:", data.salary || "", "", "", ""]);
    reciboData.push(["PUESTO:", data.position || "", "", "", ""]);
    reciboData.push(["SUCURSAL:", data.branch || "", "", "", ""]);
    reciboData.push([
      "PERIODO DE PAGO:",
      `${startDateDisplay} al ${endDateDisplay}`,
      "",
      "",
      "",
    ]);
    reciboData.push(["FECHA DE PAGO:", paymentDate, "", "", ""]);
    reciboData.push([]);

    const ingresosData = [];

    const getChartEntryForDailyPayment = (day: any) => {
      const formattedDate = moment(day.date, [
        "YYYY-MM-DD",
        "DD/MM/YYYY",
      ]).format("DD-MM-YYYY");
      return chartDetailMap.get(formattedDate);
    };

    const chartCompleteDates = new Set(
      (chartDaysData?.detailed || [])
        .filter((item: any) => {
          const hours = Number(item?.horasRegistradas ?? 0);
          return hours >= fullDayHours && hours < fullDayHours * 2 - 0.5;
        })
        .map((item: any) => item.fecha)
    );

    const dailySalaryForExcel =
      Number(data.dailySalary.replace(/[^0-9.-]+/g, "")) || 0;
    const hourlyPayForExcel =
      Number(data.payPerHour.replace(/[^0-9.-]+/g, "")) || 0;

    let jornadasCompletasPuras = 0;
    let importeJornadasCompletas = 0;

    let pagosPorHoraCount = 0;
    let horasPagosPorHora = 0;
    let importePagosPorHora = 0;

    let horasExtrasCount = 0;
    let diasConExtras = 0;
    let importeHorasExtras = 0;

    let turnoDobleCount = 0;
    let importeTurnoDoble = 0;
    let vacacionesCount = 0;
    let importeVacaciones = 0;

    dailyPayments.forEach((day: any) => {
      const concept = (day.concept || "").toLowerCase();
      const hours = parseFloat(day.hours || "0");

      if (
        isReciboRestDayWorkedRow(day) ||
        isReciboRestDayEnjoyedRow(day)
      ) {
        return;
      }

      if (
        concept.includes("festivo trabajado") ||
        concept.includes("día festivo")
      ) {
        return;
      }

      if (concept.includes("vacaciones")) {
        vacacionesCount++;
        importeVacaciones += Number(day.payment) || 0;
        return;
      }

      if (concept.includes("doble jornada") || hours >= 16.5) {
        turnoDobleCount++;
        importeTurnoDoble += day.payment;
        return;
      }

      if (
        concept.includes("pago por hora") ||
        (hours > 0 && hours < fullDayHours && !concept.includes("jornada completa"))
      ) {
        pagosPorHoraCount++;
        let conceptHours = hours;
        if (concept.includes("× pago por hora")) {
          const match = concept.match(
            /(\d+(?:\.\d+)?)\s*h\s*×\s*pago por hora/i
          );
          if (match) {
            conceptHours = parseFloat(match[1]);
          }
        }
        horasPagosPorHora += conceptHours;
        return;
      }

      if (concept.includes("jornada completa") && concept.includes("extras")) {
        jornadasCompletasPuras++;
        importeJornadasCompletas += dailySalaryForExcel;
        return;
      }

      if (concept.includes("jornada completa")) {
        jornadasCompletasPuras++;
        importeJornadasCompletas += dailySalaryForExcel;
        return;
      }

      if (hours >= fullDayHours) {
        jornadasCompletasPuras++;
        importeJornadasCompletas += dailySalaryForExcel;
      }
    });
    importePagosPorHora = horasPagosPorHora * hourlyPayForExcel;

    horasExtrasCount = sumExtraHoursFromDailyPayments(
      dailyPayments,
      fullDayHours
    );
    diasConExtras = countDailyPaymentsWithAcceptedExtras(
      dailyPayments,
      fullDayHours
    );
    importeHorasExtras = horasExtrasCount * hourlyPayForExcel;

    const jornadasCuadro = getReciboJornadasCompletasCuadroDisplay(
      dailyPayments,
      fullDayHours
    );

    ingresosData.push([
      "Jornadas completas",
      jornadasCuadro.dias > 0 ? jornadasCuadro.dias : "",
      jornadasCuadro.dias > 0
        ? jornadasCuadro.horasNominales.toFixed(1)
        : "",
      importeJornadasCompletas > 0
        ? getCurrencyFormat(importeJornadasCompletas)
        : "",
    ]);

    ingresosData.push([
      "Pagos por hora",
      pagosPorHoraCount > 0 ? pagosPorHoraCount : "",
      horasPagosPorHora > 0 ? horasPagosPorHora.toFixed(1) : "",
      importePagosPorHora > 0 ? getCurrencyFormat(importePagosPorHora) : "",
    ]);

    ingresosData.push([
      "Horas extras",
      diasConExtras > 0 ? diasConExtras : "",
      horasExtrasCount > 0 ? horasExtrasCount.toFixed(1) : "",
      importeHorasExtras > 0 ? getCurrencyFormat(importeHorasExtras) : "",
    ]);

    ingresosData.push([
      "Turno doble",
      turnoDobleCount > 0 ? turnoDobleCount : "",
      turnoDobleCount > 0 ? (turnoDobleCount * fullDayHours * 2).toFixed(1) : "",
      importeTurnoDoble > 0 ? getCurrencyFormat(importeTurnoDoble) : "",
    ]);

    ingresosData.push([
      "Vacaciones 25%",
      vacacionesCount > 0 ? vacacionesCount : "",
      vacacionesCount > 0 ? (vacacionesCount * fullDayHours).toFixed(1) : "",
      importeVacaciones > 0 ? getCurrencyFormat(importeVacaciones) : "",
    ]);

    const freeDaysEnjoyedCountExcel = allDailyPayments.filter((day: any) =>
      isReciboRestDayEnjoyedRow(day)
    ).length;

    // Usar directamente los valores de calculatedFreeDays y paymentCorrected
    const calculatedFreeDaysWorked = calculatedFreeDays.freeDaysWorked;
    const calculatedFreeDaysEnjoyed = freeDaysEnjoyedCountExcel;

    const horasDiasDescansoTrabajados =
      calculatedFreeDaysWorked > 0 ? calculatedFreeDaysWorked * fullDayHours : 0;
    ingresosData.push([
      "Días de descanso trabajados",
      calculatedFreeDaysWorked > 0 ? calculatedFreeDaysWorked : "",
      horasDiasDescansoTrabajados > 0
        ? horasDiasDescansoTrabajados.toFixed(1)
        : "",
      calculatedFreeDaysWorked > 0
        ? paymentCorrected.freeDayWorkedPayTotal
        : "",
    ]);

    const horasDiasDescansoDisfrutados =
      calculatedFreeDaysEnjoyed > 0 ? calculatedFreeDaysEnjoyed * fullDayHours : 0;

    const calculatedFreeDaysEnjoyedPay =
      calculatedFreeDaysEnjoyed * dailySalaryForExcel;
    ingresosData.push([
      "Días de descanso disfrutados",
      calculatedFreeDaysEnjoyed > 0 ? calculatedFreeDaysEnjoyed : "",
      horasDiasDescansoDisfrutados > 0
        ? horasDiasDescansoDisfrutados.toFixed(1)
        : "",
      calculatedFreeDaysEnjoyed > 0
        ? getCurrencyFormat(calculatedFreeDaysEnjoyedPay)
        : "",
    ]);

    const hasBonos =
      paymentCorrected.totalIncentives &&
      Number(paymentCorrected.totalIncentives.replace(/[^0-9.-]+/g, "")) > 0;
    ingresosData.push([
      "Bonos",
      "",
      "",
      hasBonos ? paymentCorrected.totalIncentives : "",
    ]);

    const celebrityDaysWorkedCount = data.celebrityDaysWorked || 0;
    const celebrityDaysPayTotalValue =
      getCelebrityDaysTotalFromBreakdown() ||
      Number((data.celebrityDaysPayTotal || "0").replace(/[^0-9.-]+/g, "")) ||
      0;
    const horasDiasFestivosTrabajados =
      celebrityDaysWorkedCount > 0 ? celebrityDaysWorkedCount * fullDayHours : 0;
    ingresosData.push([
      "Días festivos trabajados",
      celebrityDaysWorkedCount > 0 ? celebrityDaysWorkedCount : "",
      horasDiasFestivosTrabajados > 0
        ? horasDiasFestivosTrabajados.toFixed(1)
        : "",
      celebrityDaysWorkedCount > 0
        ? getCurrencyFormat(celebrityDaysPayTotalValue)
        : "",
    ]);

    const horasDiasFestivos =
      data.totalHollydays > 0 ? data.totalHollydays * fullDayHours : 0;
    ingresosData.push([
      "Días festivos",
      data.totalHollydays > 0 ? data.totalHollydays : "",
      horasDiasFestivos > 0 ? horasDiasFestivos.toFixed(1) : "",
      data.totalHollydays > 0
        ? getCurrencyFormat(
            data.totalHollydays *
              Number(data.dailySalary?.replace(/[^0-9.-]+/g, "") || 0)
          )
        : "",
    ]);

    ingresosData.push(["Incapacidades", "", "", ""]);
    ingresosData.push(["Aguinaldo", "", "", ""]);
    ingresosData.push(["Vacaciones", "", "", ""]);
    ingresosData.push(["Prima vacacional", "", "", ""]);
    ingresosData.push(["Otros", "", "", ""]);
    const { rows: absenceOtrosRows, totalAmount: absenceOtrosTotal } =
      buildAbsenceOtrosIngresoRows(allDailyPayments, fullDayHours, getCurrencyFormat);
    absenceOtrosRows.forEach((row) => ingresosData.push(row));

    const deduccionesAplicadas = getAppliedMissingBoxes();
    const nuevasDeducciones = getAppliedNewDeductions();

    const rawDeductionLines: { name: string; amount: number }[] = [];

    const breakdownSource =
      fetchedData?.paymentDetails?.deductionBreakdown ??
      paymentDetails?.deductionBreakdown ??
      (payment as any)?.deductionBreakdown ??
      [];

    if (Array.isArray(breakdownSource) && breakdownSource.length > 0) {
      breakdownSource.forEach((d: any) => {
        const amt = parseDeductionAmount(d.amount ?? d.calculatedAmount);
        if (amt > 0) {
          const label = String(d.name ?? "Sin nombre").trim() || "Sin nombre";
          rawDeductionLines.push({ name: label, amount: amt });
        }
      });
    } else {
      deduccionesAplicadas.forEach((deduccion) => {
        if (deduccion.calculatedAmount > 0) {
          const label =
            String(deduccion.name ?? "Sin nombre").trim() || "Sin nombre";
          rawDeductionLines.push({
            name: label,
            amount: deduccion.calculatedAmount,
          });
        }
      });
      nuevasDeducciones.forEach((deduccion) => {
        if (deduccion.calculatedAmount > 0) {
          const label =
            String(deduccion.name ?? "Sin nombre").trim() || "Sin nombre";
          rawDeductionLines.push({
            name: label,
            amount: deduccion.calculatedAmount,
          });
        }
      });
    }

    if (data.uniformDeduction) {
      const u = parseDeductionAmount(data.uniformDeduction);
      if (u > 0) {
        rawDeductionLines.push({ name: "Uniforme", amount: u });
      }
    }

    const groupedDeductions = groupDeductionsForRecibo(rawDeductionLines);
    let sumDetailed = groupedDeductions.reduce((s, r) => s + r.amount, 0);
    sumDetailed = Math.round(sumDetailed * 100) / 100;

    const totalDeduccionesBackend =
      Number(
        (paymentCorrected.totalDeductions || "0")
          .toString()
          .replace(/[^0-9.-]+/g, "")
      ) || 0;
    const backendRounded = Math.round(totalDeduccionesBackend * 100) / 100;

    const deduccionesData: (string | number)[][] = groupedDeductions.map(
      (row) => [row.displayName, getCurrencyFormat(row.amount)]
    );

    if (backendRounded > sumDetailed + 0.01) {
      deduccionesData.push([
        "Otros",
        getCurrencyFormat(backendRounded - sumDetailed),
      ]);
    } else if (groupedDeductions.length === 0 && backendRounded > 0) {
      deduccionesData.push([
        "Otros",
        getCurrencyFormat(backendRounded),
      ]);
    }

    const hollydaysTotal =
      data.totalHollydays > 0
        ? data.totalHollydays *
          Number(data.dailySalary?.replace(/[^0-9.-]+/g, "") || 0)
        : 0;

    const totalIngresos =
      (importeJornadasCompletas || 0) +
      (importePagosPorHora || 0) +
      (importeHorasExtras || 0) +
      (importeTurnoDoble || 0) +
      Number(
        paymentCorrected.freeDayWorkedPayTotal?.replace(/[^0-9.-]+/g, "") || 0
      ) +
      freeDaysEnjoyedPayAmount +
      Number(paymentCorrected.totalIncentives?.replace(/[^0-9.-]+/g, "") || 0) +
      celebrityDaysPayTotalValue +
      hollydaysTotal +
      absenceOtrosTotal;

    const totalDeducciones = Number(
      paymentCorrected.totalDeductions?.replace(/[^0-9.-]+/g, "") || 0
    );

    const totalAPagar =
      Number(
        (paymentCorrected.hoursAuthorizedPay || "0" || 0)
          .toString()
          .replace(/[^0-9.-]+/g, "")
      ) +
      Number(
        (paymentCorrected.totalIncentives || "0" || 0)
          .toString()
          .replace(/[^0-9.-]+/g, "")
      ) +
      hollydaysTotal -
      Number(
        (paymentCorrected.totalDeductions || "0" || 0)
          .toString()
          .replace(/[^0-9.-]+/g, "")
      );
    const netoRecibido = totalAPagar;

    const maxRows = Math.max(ingresosData.length, deduccionesData.length);

    reciboData.push(["INGRESOS", "", "", "", "DEDUCCIONES", ""]);
    reciboData.push([
      "CONCEPTO",
      "DÍAS",
      "HORAS",
      "IMPORTE",
      "CONCEPTO",
      "IMPORTE",
    ]);

    for (let i = 0; i < maxRows; i++) {
      const ingreso = ingresosData[i] || ["", "", "", ""];
      const deduccion = deduccionesData[i] || ["", ""];

      reciboData.push([
        ingreso[0],
        ingreso[1],
        ingreso[2],
        ingreso[3],
        deduccion[0],
        deduccion[1],
      ]);
    }

    reciboData.push([
      "INGRESOS TOTALES",
      "",
      "",
      getCurrencyFormat(totalIngresos),
      "DEDUCCIONES TOTALES",
      getCurrencyFormat(totalDeducciones),
    ]);
    reciboData.push([]);

    reciboData.push([
      `PAGO NETO RECIBIDO: ${getCurrencyFormat(netoRecibido)}`,
      "",
      "",
      "",
      "",
    ]);
    reciboData.push([]);

    reciboData.push([
      "Recibí la cantidad anotada en este recibo en pago de mi sueldo.",
    ]);
    reciboData.push([
      "Además certifico que no se me adeuda a la fecha, cantidad alguna por ningún concepto.",
    ]);
    reciboData.push([]);
    reciboData.push(["NOMBRE COMPLETO Y FIRMA:", ""]);
    reciboData.push(["_________________________", ""]);

    const worksheet = XLSX.utils.aoa_to_sheet(reciboData);

    const columnWidths = [
      { wch: 25 },
      { wch: 8 },
      { wch: 10 },
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
    ];
    worksheet["!cols"] = columnWidths;

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },

      { s: { r: 2, c: 1 }, e: { r: 2, c: 5 } },
      { s: { r: 3, c: 1 }, e: { r: 3, c: 5 } },
      { s: { r: 4, c: 1 }, e: { r: 4, c: 5 } },
      { s: { r: 5, c: 1 }, e: { r: 5, c: 5 } },
      { s: { r: 6, c: 1 }, e: { r: 6, c: 5 } },
      { s: { r: 7, c: 1 }, e: { r: 7, c: 5 } },
    ];

    if (worksheet["A1"]) {
      worksheet["A1"].s = {
        font: { bold: true, size: 20, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: "1F4E79" } },
        border: {
          top: { style: "thick", color: { rgb: "FFFFFF" } },
          bottom: { style: "thick", color: { rgb: "FFFFFF" } },
          left: { style: "thick", color: { rgb: "FFFFFF" } },
          right: { style: "thick", color: { rgb: "FFFFFF" } },
        },
      };
    }

    for (let row = 3; row <= 8; row++) {
      if (worksheet[`A${row}`]) {
        worksheet[`A${row}`].s = {
          font: { bold: true, size: 12, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "2F5597" } },
          alignment: { horizontal: "left", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "FFFFFF" } },
            bottom: { style: "thin", color: { rgb: "FFFFFF" } },
            left: { style: "thin", color: { rgb: "FFFFFF" } },
            right: { style: "thin", color: { rgb: "FFFFFF" } },
          },
        };
      }

      if (worksheet[`B${row}`]) {
        worksheet[`B${row}`].s = {
          font: { size: 12, color: { rgb: "333333" } },
          fill: { fgColor: { rgb: "F8F9FA" } },
          alignment: { horizontal: "left", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "FFFFFF" } },
            bottom: { style: "thin", color: { rgb: "FFFFFF" } },
            left: { style: "thin", color: { rgb: "FFFFFF" } },
            right: { style: "thin", color: { rgb: "FFFFFF" } },
          },
        };
      }
    }

    let tablaStartRow = 0;
    for (let row = 1; row <= range.e.r; row++) {
      if (worksheet[`A${row}`] && worksheet[`A${row}`].v === "INGRESOS") {
        tablaStartRow = row;
        break;
      }
    }

    if (tablaStartRow > 0) {
      worksheet["!merges"].push(
        {
          s: { r: tablaStartRow - 1, c: 0 },
          e: { r: tablaStartRow - 1, c: 3 },
        },
        { s: { r: tablaStartRow - 1, c: 4 }, e: { r: tablaStartRow - 1, c: 5 } }
      );

      ["A", "E"].forEach((col) => {
        if (worksheet[`${col}${tablaStartRow}`]) {
          worksheet[`${col}${tablaStartRow}`].s = {
            font: { bold: true, size: 16, color: { rgb: "FFFFFF" } },
            alignment: { horizontal: "center", vertical: "center" },
            fill: { fgColor: { rgb: "2F5597" } },
            border: {
              top: { style: "thick", color: { rgb: "FFFFFF" } },
              bottom: { style: "thick", color: { rgb: "FFFFFF" } },
              left: { style: "thick", color: { rgb: "FFFFFF" } },
              right: { style: "thick", color: { rgb: "FFFFFF" } },
            },
          };
        }
      });

      const headerRow = tablaStartRow + 1;
      ["A", "B", "C", "D", "E", "F"].forEach((col) => {
        if (worksheet[`${col}${headerRow}`]) {
          worksheet[`${col}${headerRow}`].s = {
            font: { bold: true, size: 13, color: { rgb: "FFFFFF" } },
            alignment: { horizontal: "center", vertical: "center" },
            fill: { fgColor: { rgb: "1F4E79" } },
            border: {
              top: { style: "thick", color: { rgb: "FFFFFF" } },
              bottom: { style: "thick", color: { rgb: "FFFFFF" } },
              left: { style: "thin", color: { rgb: "FFFFFF" } },
              right: { style: "thin", color: { rgb: "FFFFFF" } },
            },
          };
        }
      });

      for (let row = headerRow + 1; row <= range.e.r; row++) {
        const isTotalRow =
          worksheet[`A${row}`] &&
          (worksheet[`A${row}`].v === "INGRESOS TOTALES" ||
            worksheet[`A${row}`].v === "DEDUCCIONES TOTALES");

        const isNetoRow =
          worksheet[`A${row}`] &&
          worksheet[`A${row}`].v &&
          String(worksheet[`A${row}`].v).includes("PAGO NETO RECIBIDO  :");

        if (isNetoRow) {
          worksheet["!merges"].push({
            s: { r: row - 1, c: 0 },
            e: { r: row - 1, c: 5 },
          });
        }

        ["A", "B", "C", "D", "E", "F"].forEach((col) => {
          if (worksheet[`${col}${row}`]) {
            if (isTotalRow) {
              worksheet[`${col}${row}`].s = {
                font: { bold: true, size: 12, color: { rgb: "FFFFFF" } },
                alignment: { horizontal: "center", vertical: "center" },
                fill: { fgColor: { rgb: "1F4E79" } },
                border: {
                  top: { style: "thick", color: { rgb: "FFFFFF" } },
                  bottom: { style: "thick", color: { rgb: "FFFFFF" } },
                  left: { style: "thin", color: { rgb: "FFFFFF" } },
                  right: { style: "thin", color: { rgb: "FFFFFF" } },
                },
              };
            } else if (isNetoRow) {
              worksheet[`${col}${row}`].s = {
                font: { bold: true, size: 16, color: { rgb: "FFFFFF" } },
                alignment: { horizontal: "center", vertical: "center" },
                fill: { fgColor: { rgb: "2F5597" } },
                border: {
                  top: { style: "thick", color: { rgb: "FFFFFF" } },
                  bottom: { style: "thick", color: { rgb: "FFFFFF" } },
                  left: { style: "thick", color: { rgb: "FFFFFF" } },
                  right: { style: "thick", color: { rgb: "FFFFFF" } },
                },
              };
            } else {
              const isEvenRow = (row - headerRow) % 2 === 0;
              worksheet[`${col}${row}`].s = {
                font: { size: 11, color: { rgb: "333333" } },
                alignment: { horizontal: "left", vertical: "center" },
                fill: { fgColor: { rgb: isEvenRow ? "F8F9FA" : "FFFFFF" } },
                border: {
                  top: { style: "thin", color: { rgb: "D0D0D0" } },
                  bottom: { style: "thin", color: { rgb: "D0D0D0" } },
                  left: { style: "thin", color: { rgb: "D0D0D0" } },
                  right: { style: "thin", color: { rgb: "D0D0D0" } },
                },
              };

              if (col === "B" || col === "C" || col === "D" || col === "F") {
                worksheet[`${col}${row}`].s.alignment.horizontal = "center";
                worksheet[`${col}${row}`].s.font.bold = true;
              }
            }
          }
        });
      }
    }

    for (let row = range.e.r - 4; row <= range.e.r; row++) {
      if (worksheet[`A${row}`]) {
        if (worksheet[`A${row}`].v === "_________________________") {
          worksheet[`A${row}`].s = {
            font: { size: 12, color: { rgb: "333333" } },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              bottom: { style: "thin", color: { rgb: "333333" } },
            },
          };
        } else {
          worksheet[`A${row}`].s = {
            font: { size: 11, italic: true, color: { rgb: "333333" } },
            alignment: { horizontal: "left", vertical: "center" },
          };
        }
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Recibo de Nómina");

    const uniformNumber =
      Number((data.uniformDeduction || "0").replace(/[^0-9.-]+/g, "")) || 0;
    const celebrityTotalBreak = getCelebrityDaysTotalFromBreakdown();
    const totalIngresosTabla = totalIngresos;
    const totalDeduccionesTabla = totalDeducciones;

    const checadasHeader = [
      ["CHECADAS Y DESGLOSE COMPLETO", "", "", "", "", "", ""],
      [
        "EMPLEADO",
        data.name || "",
        "PUESTO",
        data.position || "",
        "SUCURSAL",
        data.branch || "",
        "PERIODO",
      ],
      ["", "", "", "", "", "", `${startDateDisplay} al ${endDateDisplay}`],
      [],
      [
        "Fecha",
        "Horas trabajadas",
        "En Horario",
        "Tiempo trabajado",
        "Concepto",
        "Pago del día",
        "Entrada",
        "Salida",
      ],
    ];

    const checadasRows: any[] = [];
    if (dailyPayments && dailyPayments.length > 0) {
      for (const day of dailyPayments) {
        checadasRows.push([
          day.date,
          formatPayrollHoursWithH(day.hours),
          formatPayrollHoursWithH(day.hoursInSchedule || 0),
          formatHoursToTime(
            parseFloat(day.hoursForDisplay || day.hoursInSchedule || day.hours)
          ),
          day.concept,
          getCurrencyFormat(day.payment),
          day.checkIn,
          day.checkOut,
        ]);
      }
    }

    checadasRows.push([]);
    checadasRows.push(["Bonos", "", "", "", "", "", ""]);
    checadasRows.push([
      "Nombre",
      "Frecuencia",
      "Período",
      "Monto Base",
      "Monto Aplicado",
      "",
      "",
    ]);
    for (const bonus of getAppliedBonuses()) {
      const startDate =
        bonus.startDate &&
        bonus.startDate !== "null" &&
        bonus.startDate !== null &&
        bonus.startDate !== undefined
          ? bonus.startDate
          : "";
      const endDate =
        bonus.endDate &&
        bonus.endDate !== "null" &&
        bonus.endDate !== null &&
        bonus.endDate !== undefined
          ? bonus.endDate
          : "";
      const periodo = startDate || endDate ? `${startDate} - ${endDate}` : "-";
      checadasRows.push([
        bonus.name,
        bonus.frequency,
        periodo,
        getCurrencyFormat(Number(bonus.amount || 0)),
        getCurrencyFormat(bonus.calculatedAmount || 0),
        "",
        "",
      ]);
    }

    checadasRows.push([]);
    checadasRows.push(["Deducciones", "", "", "", "", "", ""]);
    checadasRows.push([
      "Nombre",
      "Frecuencia",
      "Período",
      "Monto Base",
      "Monto Aplicado",
      "",
      "",
    ]);

    for (const mb of getAppliedMissingBoxes()) {
      const startDate =
        mb.startDate && mb.startDate !== "null" ? mb.startDate : "";
      const endDate = mb.endDate && mb.endDate !== "null" ? mb.endDate : "";
      const periodo = startDate || endDate ? `${startDate} - ${endDate}` : "-";
      checadasRows.push([
        mb.name,
        mb.frequency,
        periodo,
        getCurrencyFormat(Number(mb.amount || 0)),
        getCurrencyFormat(mb.calculatedAmount || 0),
        "",
        "",
      ]);
    }

    for (const nd of getAppliedNewDeductions()) {
      const startDate =
        nd.startDate && nd.startDate !== "null" ? nd.startDate : "";
      const endDate = nd.endDate && nd.endDate !== "null" ? nd.endDate : "";
      const periodo = startDate || endDate ? `${startDate} - ${endDate}` : "-";
      checadasRows.push([
        nd.name,
        nd.frequency,
        periodo,
        getCurrencyFormat(Number(nd.amount || 0)),
        getCurrencyFormat(nd.calculatedAmount || 0),
        "",
        "",
      ]);
    }

    if (uniformNumber > 0) {
      checadasRows.push([
        "Uniforme",
        "quincenal",
        "Desde ingreso",
        getCurrencyFormat(uniformNumber),
        getCurrencyFormat(uniformNumber),
        "",
        "",
      ]);
    }

    checadasRows.push([]);

    const checadasSheet = XLSX.utils.aoa_to_sheet([
      ...checadasHeader,
      ...checadasRows,
    ]);
    checadasSheet["!cols"] = [
      { wch: 12 },
      { wch: 16 },
      { wch: 12 },
      { wch: 16 },
      { wch: 45 },
      { wch: 16 },
      { wch: 14 },
      { wch: 14 },
    ];

    XLSX.utils.book_append_sheet(
      workbook,
      checadasSheet,
      "Detalle asistencias"
    );

    const fileName = `Recibo Nomina ${data.name || "Empleado"}_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  if (loading) {
    return (
      <div className="text-center">
        <i className="fa fa-spinner fa-spin fa-3x"></i>
        <p>Cargando datos de pago...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div
          onClick={() => handleNormalCalculationNominaChange(!NormalCalculationNomina)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
            padding: "6px 12px",
            borderRadius: "10px",
            background: NormalCalculationNomina
              ? "rgba(80, 183, 192, 0.1)"
              : "#f8fafc",
            border: `1px solid ${NormalCalculationNomina ? "#50B7C0" : "#e2e8f0"}`,
            transition: "all 0.2s ease",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "20px",
              borderRadius: "10px",
              background: NormalCalculationNomina
                ? "linear-gradient(135deg, #50B7C0 0%, #3a9ea6 100%)"
                : "#cbd5e1",
              position: "relative",
              transition: "background 0.2s ease",
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                background: "#fff",
                position: "absolute",
                top: "2px",
                left: NormalCalculationNomina ? "18px" : "2px",
                transition: "left 0.2s ease",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}
            />
          </div>
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: NormalCalculationNomina ? "#50B7C0" : "#64748b",
            }}
          >
            Nómina Normal
          </span>
        </div>
        <button
          type="button"
          className="border-0 d-flex align-items-center"
          onClick={exportToExcel}
          title="Exportar a Excel"
          style={{
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "#ffffff",
            borderRadius: "12px",
            padding: "0.35rem 0.75rem",
            fontSize: "0.75rem",
            fontWeight: 500,
            transition: "all 0.2s ease",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          <i
            className="fas fa-download me-1"
            style={{ fontSize: "0.7rem" }}
          ></i>
          Excel
        </button>
      </div>

      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
          marginBottom: "1rem",
        }}
      >
        {/* Header colapsable */}
        <div
          onClick={() => setIsConceptsExpanded(!isConceptsExpanded)}
          style={{
            background: "linear-gradient(135deg, #50B7C0 0%, #3a9ea6 100%)",
            padding: "8px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i
              className={`fas fa-chevron-${isConceptsExpanded ? "down" : "right"}`}
              style={{
                color: "#fff",
                fontSize: "0.6rem",
                transition: "transform 0.2s",
              }}
            ></i>
            <span
              style={{ color: "#fff", fontWeight: 600, fontSize: "0.7rem" }}
            >
              DESGLOSE DE PAGO
            </span>
          </div>
          <div style={{ display: "flex", gap: "30px" }}>
            <span
              style={{
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.7rem",
                minWidth: "80px",
                textAlign: "center",
              }}
            >
              VALOR
            </span>
            <span
              style={{
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.7rem",
                minWidth: "70px",
                textAlign: "right",
              }}
            >
              MONTO
            </span>
          </div>
        </div>

        {/* Conceptos colapsables */}
        <div
          style={{
            maxHeight: isConceptsExpanded ? "500px" : "0",
            overflow: "hidden",
            transition: "max-height 0.3s ease",
          }}
        >
          {conceptos
            .filter(
              ({ key }) =>
                key !== "totalIncentives" && key !== "totalDeductions"
            )
            .map(({ key, label, value, money }, index) => {
              const isEven = index % 2 === 0;

              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 14px",
                    backgroundColor: isEven ? "#fff" : "#f8fafc",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <span
                    style={{
                      color: "#334155",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                    }}
                  >
                    {label}
                  </span>
                  <div style={{ display: "flex", gap: "30px" }}>
                    <span
                      style={{
                        color: "#64748b",
                        fontSize: "0.75rem",
                        minWidth: "80px",
                        textAlign: "center",
                      }}
                    >
                      {value(paymentCorrected, data, fullDayHours)}
                    </span>
                    <span
                      style={{
                        color: "#1e293b",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        minWidth: "70px",
                        textAlign: "right",
                      }}
                    >
                      {money(paymentCorrected, data)}
                    </span>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Incentivos y Deducciones siempre visibles */}
        <div>
          {conceptos
            .filter(
              ({ key }) =>
                key === "totalIncentives" || key === "totalDeductions"
            )
            .map(({ key, label, value, money }) => {
              const isDeduction = key === "totalDeductions";
              const isIncentive = key === "totalIncentives";

              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 14px",
                    backgroundColor: isDeduction
                      ? "rgba(239, 68, 68, 0.05)"
                      : "rgba(34, 197, 94, 0.05)",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <span
                    style={{
                      color: isDeduction ? "#dc2626" : "#16a34a",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <i
                      className={`fas fa-${
                        isDeduction ? "minus" : "plus"
                      }-circle`}
                      style={{ fontSize: "0.65rem" }}
                    ></i>
                    {label}
                  </span>
                  <div style={{ display: "flex", gap: "30px" }}>
                    <span
                      style={{
                        color: "#64748b",
                        fontSize: "0.75rem",
                        minWidth: "80px",
                        textAlign: "center",
                      }}
                    >
                      {value(paymentCorrected, data, fullDayHours)}
                    </span>
                    <span
                      style={{
                        color: isDeduction ? "#dc2626" : "#16a34a",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        minWidth: "70px",
                        textAlign: "right",
                      }}
                    >
                      {money(paymentCorrected, data)}
                    </span>
                  </div>
                </div>
              );
            })}
        </div>

        <div
          style={{
            background: "#f1f5f9",
            padding: "8px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <span
            style={{ color: "#1e293b", fontWeight: 700, fontSize: "0.75rem" }}
          >
            TOTAL A PAGAR
          </span>
          <span
            style={{ color: "#1e293b", fontWeight: 700, fontSize: "0.9rem" }}
          >
            {getCurrencyFormat(
              Number(
                (paymentCorrected.hoursAuthorizedPay || "0" || 0)
                  .toString()
                  .replace(/[^0-9.-]+/g, "")
              ) +
                Number(
                  (paymentCorrected.totalIncentives || "0" || 0)
                    .toString()
                    .replace(/[^0-9.-]+/g, "")
                ) +
                (data.totalHollydays > 0
                  ? data.totalHollydays *
                    Number(data.dailySalary?.replace(/[^0-9.-]+/g, "") || 0)
                  : 0) -
                Number(
                  (paymentCorrected.totalDeductions || "0" || 0)
                    .toString()
                    .replace(/[^0-9.-]+/g, "")
                )
            )}
          </span>
        </div>

        {/* Pago Estimado calculado igual que en el backend */}
        <div
          style={{
            background: "#fff",
            borderTop: "2px solid #50B7C0",
            padding: "12px 14px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span
              style={{
                color: "#1F4E79",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            >
              PAGO ESTIMADO 
            </span>
            <span
              style={{
                color: "#1F4E79",
                fontWeight: 700,
                fontSize: "0.9rem",
              }}
            >
              {calculateTotalPayWithIncentives.totalPayWithIncentivesFormatted}
            </span>
          </div>
          <div
            style={{
              padding: "8px",
              background: "#f8fafc",
              borderRadius: "6px",
              fontSize: "0.7rem",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                color: "#475569",
              }}
            >
              <span>Total Pagos Diarios:</span>
              <span style={{ fontWeight: 600 }}>
                {getCurrencyFormat(calculateTotalPayWithIncentives.totalDailyPayments)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                color: "#475569",
              }}
            >
              <span>+ Total Incentivos:</span>
              <span style={{ fontWeight: 600, color: "#16a34a" }}>
                +{getCurrencyFormat(calculateTotalPayWithIncentives.totalIncentives)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                color: "#475569",
              }}
            >
              <span>- Total Deducciones:</span>
              <span style={{ fontWeight: 600, color: "#dc2626" }}>
                -{getCurrencyFormat(calculateTotalPayWithIncentives.totalDeductions)}
              </span>
            </div>
            {calculateTotalPayWithIncentives.totalMissingBoxDeductions > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "4px",
                  color: "#475569",
                }}
              >
                <span>- Faltantes en Caja:</span>
                <span style={{ fontWeight: 600, color: "#dc2626" }}>
                  -{getCurrencyFormat(calculateTotalPayWithIncentives.totalMissingBoxDeductions)}
                </span>
              </div>
            )}
            {calculateTotalPayWithIncentives.uniformDeduction > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "4px",
                  color: "#475569",
                }}
              >
                <span>- Deducción Uniforme:</span>
                <span style={{ fontWeight: 600, color: "#dc2626" }}>
                  -{getCurrencyFormat(calculateTotalPayWithIncentives.uniformDeduction)}
                </span>
              </div>
            )}
            <div
              style={{
                borderTop: "1px solid #cbd5e1",
                marginTop: "6px",
                paddingTop: "6px",
                display: "flex",
                justifyContent: "space-between",
                color: "#1F4E79",
                fontWeight: 700,
              }}
            >
              <span>= Pago Estimado:</span>
              <span>{calculateTotalPayWithIncentives.totalPayWithIncentivesFormatted}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: "1rem" }}>
        <div
          style={{
            display: "flex",
            gap: "4px",
            background: "#f1f5f9",
            padding: "4px",
            borderRadius: "10px",
            width: "fit-content",
          }}
        >
          {[
            { id: "desglose", label: "Desglose", icon: "fa-list" },
            { id: "bonos", label: "Bonos", icon: "fa-gift" },
            { id: "deducciones", label: "Deducciones", icon: "fa-minus" },
            { id: "celebrity-days", label: "Festivos", icon: "fa-star" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "6px 12px",
                fontSize: "0.7rem",
                fontWeight: 500,
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                background:
                  activeTab === tab.id
                    ? "linear-gradient(135deg, #50B7C0 0%, #3a9ea6 100%)"
                    : "transparent",
                color: activeTab === tab.id ? "#fff" : "#64748b",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <i
                className={`fas ${tab.icon}`}
                style={{ fontSize: "0.6rem" }}
              ></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            marginTop: "8px",
            overflow: "hidden",
          }}
        >
          {activeTab === "desglose" && (
            <div>
              {allDailyPayments.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.72rem",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: "#e2e8f0",
                          borderBottom: "2px solid #cbd5e1",
                        }}
                      >
                        {[
                          "Fecha",
                          "Horas",
                          "En Horario",
                          "Tiempo",
                          "Concepto",
                          "Pago",
                          "Entrada",
                          "Salida",
                          "",
                        ].map((h) => (
                          <th
                            key={h || "actions"}
                            style={{
                              padding: "6px 10px",
                              color: "#475569",
                              fontWeight: 600,
                              textAlign: h === "Pago" ? "right" : "center",
                              whiteSpace: "nowrap",
                              fontSize: "0.7rem",
                              width: h === "" ? "40px" : "auto",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allDailyPayments.map((day, index) => {
                        const isEditing = editingRowIndex === index;
                        const editedData = editedPayments[index];
                        const displayConcept =
                          editedData?.concept ?? day.concept;
                        const displayPayment =
                          editedData?.payment ?? day.payment;
                        const rowDateKeyDDMM =
                          paymentDetailRowDateKeyDDMMYYYY(String(day.date));
                        const showLeaderPendingDot =
                          pendingLeaderReviewDates.has(rowDateKeyDDMM);
                        if (
                          savingRowIndex === index &&
                          !day.isFreeDayEnjoyed
                        ) {
                          return (
                            <tr
                              key={`row-saving-${index}`}
                              style={{
                                background: "#f8fafc",
                                borderBottom: "1px solid #e2e8f0",
                              }}
                            >
                              <td
                                colSpan={9}
                                style={{
                                  padding: "14px 12px",
                                  textAlign: "center",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "12px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <i
                                    className="fa fa-spinner fa-spin"
                                    style={{
                                      color: "#50B7C0",
                                      fontSize: "1rem",
                                    }}
                                  />
                                  <div
                                    style={{
                                      display: "flex",
                                      flex: "1 1 200px",
                                      maxWidth: 420,
                                      gap: "8px",
                                      alignItems: "center",
                                    }}
                                  >
                                    {[0.45, 0.7, 0.55, 0.8, 0.5].map(
                                      (w, si) => (
                                        <div
                                          key={si}
                                          style={{
                                            flex: w,
                                            height: 10,
                                            borderRadius: 4,
                                            background: "#e2e8f0",
                                          }}
                                        />
                                      )
                                    )}
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "0.72rem",
                                      color: "#64748b",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Guardando…
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        if (day.isFreeDayEnjoyed && !isEditing) {
                          return (
                            <tr
                              key={`free-day-${index}`}
                              style={{ background: "#f0fdfa" }}
                            >
                              <td
                                style={{
                                  padding: "6px 10px",
                                  textAlign: "center",
                                  fontWeight: 500,
                                }}
                              >
                                {formatPayrollDisplayDate(day.date)}
                                {showLeaderPendingDot && <PendingLeaderIncidentDot />}
                                {day.isPermission && (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      color: "#2563eb",
                                      fontWeight: 600,
                                      fontSize: "0.66rem",
                                    }}
                                  >
                                    (Permiso)
                                  </span>
                                )}
                                {day.isIncapacity && (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      color: "#dc2626",
                                      fontWeight: 600,
                                      fontSize: "0.66rem",
                                    }}
                                  >
                                    (Incapacidad)
                                  </span>
                                )}
                              </td>
                              <td
                                colSpan={4}
                                style={{
                                  padding: "6px 10px",
                                  textAlign: "center",
                                  fontWeight: 600,
                                  color: "#0d9488",
                                }}
                              >
                                <i
                                  className="fas fa-bed me-1"
                                  style={{ fontSize: "0.65rem" }}
                                ></i>
                                Día de descanso disfrutado
                              </td>
                              <td
                                style={{
                                  padding: "6px 10px",
                                  textAlign: "right",
                                  fontWeight: 600,
                                  color: "#0d9488",
                                }}
                              >
                                {getCurrencyFormat(displayPayment)}
                              </td>
                              <td
                                colSpan={2}
                                style={{
                                  padding: "6px 10px",
                                  textAlign: "center",
                                  color: "#94a3b8",
                                }}
                              >
                                N/A
                              </td>
                              <td
                                style={{
                                  padding: "6px 10px",
                                  textAlign: "center",
                                }}
                              >
                                <button
                                  onClick={() => {
                                    setEditingRowIndex(index);
                                    if (!editedPayments[index]) {
                                      setEditedPayments((prev) => ({
                                        ...prev,
                                        [index]: {
                                          concept: day.concept,
                                          payment: day.payment,
                                        },
                                      }));
                                    }
                                  }}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#64748b",
                                    padding: "4px",
                                  }}
                                  title="Editar"
                                >
                                  <i
                                    className="fas fa-pen"
                                    style={{ fontSize: "0.65rem" }}
                                  ></i>
                                </button>
                              </td>
                            </tr>
                          );
                        }

                        const isFreeDayWorked = day.isFreeDayWorked === true;
                        const isAbsence = day.isAbsence === true;
                        const rowBackground = isEditing
                          ? "#fef3c7"
                          : day.isFreeDayEnjoyed
                          ? "#f0fdfa"
                          : isAbsence
                          ? "#fef2f2"
                          : isFreeDayWorked
                          ? "#fff7ed"
                          : index % 2 === 0
                          ? "#fff"
                          : "#f8fafc";

                        const dayDateKey = normalizePaymentDateKey(
                          String(day.date)
                        );
                        const dayVisits =
                          Array.isArray(day.visits) && day.visits.length > 0
                            ? enrichVisitsWithBranchNames(
                                day.visits.map((v: any) =>
                                  mapStoredVisit(v, dayDateKey)
                                ),
                                effectiveRealCheckRecords
                              )
                            : visitsByFecha.get(rowDateKeyDDMM) || [];
                        const hasMultipleVisits = dayVisits.length > 1;
                        const isDayExpanded =
                          expandedPaymentDays.has(dayDateKey);

                        return (
                          <React.Fragment key={`payment-day-${index}`}>
                          <tr
                            className={
                              isDayExpanded && hasMultipleVisits
                                ? "payroll-day-expanded"
                                : undefined
                            }
                            style={{
                              background: rowBackground,
                              borderBottom:
                                isDayExpanded && hasMultipleVisits
                                  ? "none"
                                  : isAbsence
                                  ? "1px solid #fecaca"
                                  : "1px solid #e2e8f0",
                            }}
                          >
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                                fontWeight: 500,
                                cursor: hasMultipleVisits
                                  ? "pointer"
                                  : "default",
                              }}
                              onClick={() => {
                                if (hasMultipleVisits) {
                                  togglePaymentDayExpanded(dayDateKey);
                                }
                              }}
                              title={
                                hasMultipleVisits
                                  ? isDayExpanded
                                    ? "Ocultar checadas del día"
                                    : "Ver checadas del día"
                                  : undefined
                              }
                            >
                              {hasMultipleVisits && (
                                <i
                                  className={`fas fa-chevron-${
                                    isDayExpanded ? "down" : "right"
                                  } me-1`}
                                  style={{
                                    fontSize: "0.6rem",
                                    color: "#64748b",
                                  }}
                                />
                              )}
                              {formatPayrollDisplayDate(day.date)}
                              {showLeaderPendingDot && <PendingLeaderIncidentDot />}
                              {isAbsence && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    color: "#dc2626",
                                    fontWeight: 700,
                                    fontSize: "0.66rem",
                                  }}
                                >
                                  (Falta)
                                </span>
                              )}
                              {day.isPermission && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    color: "#2563eb",
                                    fontWeight: 600,
                                    fontSize: "0.66rem",
                                  }}
                                >
                                  (Permiso)
                                </span>
                              )}
                              {day.isIncapacity && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    color: "#dc2626",
                                    fontWeight: 600,
                                    fontSize: "0.66rem",
                                  }}
                                >
                                  (Incapacidad)
                                </span>
                              )}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                                color: "#64748b",
                              }}
                            >
                              {formatPayrollHoursWithH(day.hours)}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                                color: "#64748b",
                              }}
                            >
                              {formatPayrollHoursWithH(day.hoursInSchedule || 0)}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                                color: "#64748b",
                              }}
                            >
                              {formatHoursToTime(
                                parseFloat(
                                  day.hoursForDisplay ||
                                    day.hoursInSchedule ||
                                    day.hours
                                )
                              )}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                color: "#334155",
                                maxWidth: "220px",
                              }}
                            >
                              {isEditing ? (
                                editedData?.isCustom ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                      alignItems: "center",
                                    }}
                                  >
                                    <input
                                      type="text"
                                      value={displayConcept}
                                      onChange={(e) =>
                                        setEditedPayments((prev) => ({
                                          ...prev,
                                          [index]: {
                                            ...prev[index],
                                            concept: e.target.value,
                                          },
                                        }))
                                      }
                                      placeholder="Escribe el concepto..."
                                      style={{
                                        flex: 1,
                                        padding: "4px 8px",
                                        fontSize: "0.68rem",
                                        border: "1px solid #50B7C0",
                                        borderRadius: "6px",
                                        outline: "none",
                                      }}
                                    />
                                    <button
                                      onClick={() =>
                                        setEditedPayments((prev) => ({
                                          ...prev,
                                          [index]: {
                                            ...prev[index],
                                            isCustom: false,
                                          },
                                        }))
                                      }
                                      style={{
                                        background: "#94a3b8",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        color: "#fff",
                                        padding: "4px 6px",
                                        fontSize: "0.6rem",
                                      }}
                                      title="Volver a opciones"
                                    >
                                      <i className="fas fa-list"></i>
                                    </button>
                                  </div>
                                ) : (
                                  <select
                                    value={
                                      displayConcept === day.concept
                                        ? "__ORIGINAL__"
                                        : conceptOptions.some(
                                            (opt) =>
                                              opt.value === displayConcept
                                          )
                                        ? displayConcept
                                        : "__CURRENT__"
                                    }
                                    onChange={(e) => {
                                      const newConcept = e.target.value;
                                      if (newConcept === "__OTRO__") {
                                        setEditedPayments((prev) => ({
                                          ...prev,
                                          [index]: {
                                            concept: "",
                                            payment: 0,
                                            isCustom: true,
                                          },
                                        }));
                                      } else if (
                                        newConcept === "__ORIGINAL__"
                                      ) {
                                        setEditedPayments((prev) => ({
                                          ...prev,
                                          [index]: {
                                            concept: day.concept,
                                            payment: day.payment,
                                            isCustom: false,
                                          },
                                        }));
                                      } else if (newConcept !== "__CURRENT__") {
                                        const newPayment =
                                          calculatePaymentFromConcept(
                                            newConcept
                                          );
                                        setEditedPayments((prev) => ({
                                          ...prev,
                                          [index]: {
                                            concept: newConcept,
                                            payment: newPayment,
                                            isCustom: false,
                                          },
                                        }));
                                      }
                                    }}
                                    style={{
                                      width: "100%",
                                      padding: "4px 8px",
                                      fontSize: "0.68rem",
                                      border: "1px solid #50B7C0",
                                      borderRadius: "6px",
                                      outline: "none",
                                      background: "#fff",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <option value="__ORIGINAL__">
                                      Original: {day.concept} -{" "}
                                      {getCurrencyFormat(
                                        Number(day.payment) || 0
                                      )}
                                    </option>
                                    {displayConcept &&
                                      displayConcept !== day.concept &&
                                      !conceptOptions.some(
                                        (opt) => opt.value === displayConcept
                                      ) && (
                                        <option value="__CURRENT__">
                                          Actual: {displayConcept}
                                        </option>
                                      )}
                                    {conceptOptions.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.type === "otro"
                                          ? opt.label
                                          : `${opt.label} - ${getCurrencyFormat(
                                              calculatePaymentFromConcept(
                                                opt.value
                                              )
                                            )}`}
                                      </option>
                                    ))}
                                  </select>
                                )
                              ) : displayConcept &&
                                displayConcept.length > 45 ? (
                                <Tooltip title={displayConcept} arrow>
                                  <span>
                                    {displayConcept.substring(0, 45)}...
                                  </span>
                                </Tooltip>
                              ) : (
                                displayConcept
                              )}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "right",
                                fontWeight: 600,
                                color: "#1e293b",
                              }}
                            >
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={
                                    Number.isFinite(Number(displayPayment))
                                      ? Number(displayPayment)
                                      : 0
                                  }
                                  onChange={(e) =>
                                    setEditedPayments((prev) => ({
                                      ...prev,
                                      [index]: {
                                        ...prev[index],
                                        concept:
                                          prev[index]?.concept ??
                                          day.concept ??
                                          "",
                                        payment:
                                          parseFloat(e.target.value) || 0,
                                      },
                                    }))
                                  }
                                  style={{
                                    width: "80px",
                                    padding: "4px 8px",
                                    fontSize: "0.72rem",
                                    border: "1px solid #50B7C0",
                                    borderRadius: "6px",
                                    outline: "none",
                                    textAlign: "right",
                                  }}
                                />
                              ) : (
                                getCurrencyFormat(displayPayment)
                              )}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                                color: "#64748b",
                              }}
                            >
                              {day.checkIn}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                                color: "#64748b",
                              }}
                            >
                              {day.checkOut}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                              }}
                            >
                              {isEditing ? (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    justifyContent: "center",
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!day) return;
                                      void handleSaveDailyPaymentOverride(
                                        index,
                                        day
                                      );
                                    }}
                                    disabled={
                                      savingRowIndex !== null ||
                                      !partner ||
                                      !branch
                                    }
                                    style={{
                                      background: "#10b981",
                                      border: "none",
                                      borderRadius: "4px",
                                      cursor:
                                        savingRowIndex !== null ||
                                        !partner ||
                                        !branch
                                          ? "not-allowed"
                                          : "pointer",
                                      opacity:
                                        savingRowIndex !== null ||
                                        !partner ||
                                        !branch
                                          ? 0.6
                                          : 1,
                                      color: "#fff",
                                      padding: "4px 6px",
                                    }}
                                    title="Guardar"
                                  >
                                    <i
                                      className="fas fa-check"
                                      style={{ fontSize: "0.6rem" }}
                                    ></i>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingRowIndex(null);
                                      setEditedPayments((prev) => {
                                        const newState = { ...prev };
                                        delete newState[index];
                                        return newState;
                                      });
                                    }}
                                    style={{
                                      background: "#ef4444",
                                      border: "none",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      color: "#fff",
                                      padding: "4px 6px",
                                    }}
                                    title="Cancelar"
                                  >
                                    <i
                                      className="fas fa-times"
                                      style={{ fontSize: "0.6rem" }}
                                    ></i>
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => {
                                    setEditingRowIndex(index);
                                    if (!editedPayments[index]) {
                                      setEditedPayments((prev) => ({
                                        ...prev,
                                        [index]: {
                                          concept: day.concept,
                                          payment: day.payment,
                                        },
                                      }));
                                    }
                                  }}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#64748b",
                                    padding: "4px",
                                  }}
                                  title="Editar"
                                >
                                  <i
                                    className="fas fa-pen"
                                    style={{ fontSize: "0.65rem" }}
                                  ></i>
                                </button>
                              )}
                            </td>
                          </tr>
                          {isDayExpanded && hasMultipleVisits && (
                            <>
                              <PayrollDayVisitDetailRows
                                visits={dayVisits}
                                dateKey={dayDateKey}
                                variant="payment"
                              />
                              <PayrollDayVisitTotalRow
                                visits={dayVisits}
                                dateKey={dayDateKey}
                                variant="payment"
                              />
                            </>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr
                        style={{
                          background: "#f1f5f9",
                          borderTop: "1px solid #e2e8f0",
                        }}
                      >
                        <td
                          colSpan={5}
                          style={{
                            padding: "8px 10px",
                            color: "#1e293b",
                            fontWeight: 700,
                            textAlign: "right",
                            fontSize: "0.72rem",
                          }}
                        >
                          Total por días laborados
                        </td>
                        <td
                          colSpan={4}
                          style={{
                            padding: "8px 10px",
                            color: "#1e293b",
                            fontWeight: 700,
                            textAlign: "right",
                            fontSize: "0.85rem",
                          }}
                        >
                          {getCurrencyFormat(
                            allDailyPayments.reduce((sum, day, index) => {
                              const editedPayment =
                                editedPayments[index]?.payment;
                              return (
                                sum +
                                (editedPayment !== undefined
                                  ? editedPayment
                                  : day.payment)
                              );
                            }, 0)
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#94a3b8",
                    fontSize: "0.8rem",
                  }}
                >
                  No hay datos de desglose disponibles
                </p>
              )}
            </div>
          )}

          {activeTab === "bonos" && (
            <div>
              {appliedBonuses.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.72rem",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background:
                            "linear-gradient(135deg, #50B7C0 0%, #3a9ea6 100%)",
                        }}
                      >
                        {[
                          "Nombre",
                          "Frecuencia",
                          "Período",
                          "Monto Base",
                          "Aplicado",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "8px 10px",
                              color: "#fff",
                              fontWeight: 600,
                              textAlign:
                                h === "Aplicado" || h === "Monto Base"
                                  ? "right"
                                  : "center",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {appliedBonuses.map((bonus, index) => (
                        <tr
                          key={index}
                          style={{
                            background: index % 2 === 0 ? "#fff" : "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <td style={{ padding: "6px 10px", fontWeight: 500 }}>
                            {bonus.name}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "center",
                              color: "#64748b",
                            }}
                          >
                            {bonus.frequency}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "center",
                              color: "#64748b",
                            }}
                          >
                            {bonus.startDate} - {bonus.endDate}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "right",
                              color: "#64748b",
                            }}
                          >
                            {getCurrencyFormat(Number(bonus.amount))}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#16a34a",
                            }}
                          >
                            {getCurrencyFormat(bonus.calculatedAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr
                        style={{
                          background:
                            "linear-gradient(135deg, #008080 0%, #006666 100%)",
                        }}
                      >
                        <td
                          colSpan={4}
                          style={{
                            padding: "8px 10px",
                            color: "#fff",
                            fontWeight: 600,
                            textAlign: "right",
                          }}
                        >
                          Total de Bonos
                        </td>
                        <td
                          style={{
                            padding: "8px 10px",
                            color: "#fff",
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          {getCurrencyFormat(
                            appliedBonuses.reduce(
                              (sum, bonus) => sum + bonus.calculatedAmount,
                              0
                            )
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p style={paymentDetailEmptyTabStyle}>
                  Aún no hay bonos registrados para este colaborador en el
                  período.
                </p>
              )}
            </div>
          )}

          {activeTab === "deducciones" && (
            <div>
              {appliedMissingBoxes.length > 0 ||
              appliedNewDeductions.length > 0 ||
              hasUniformDeduction ? (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.72rem",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background:
                            "linear-gradient(135deg, #50B7C0 0%, #3a9ea6 100%)",
                        }}
                      >
                        {[
                          "Nombre",
                          "Frecuencia",
                          "Período",
                          "Monto Base",
                          "Aplicado",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "8px 10px",
                              color: "#fff",
                              fontWeight: 600,
                              textAlign:
                                h === "Aplicado" || h === "Monto Base"
                                  ? "right"
                                  : "center",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {appliedMissingBoxes.map((missingBox, index) => (
                        <tr
                          key={`missing-${index}`}
                          style={{
                            background: index % 2 === 0 ? "#fff" : "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <td style={{ padding: "6px 10px", fontWeight: 500 }}>
                            {missingBox.name}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "center",
                              color: "#64748b",
                            }}
                          >
                            {missingBox.frequency}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "center",
                              color: "#64748b",
                            }}
                          >
                            {missingBox.startDate} - {missingBox.endDate}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "right",
                              color: "#64748b",
                            }}
                          >
                            {getCurrencyFormat(Number(missingBox.amount))}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#dc2626",
                            }}
                          >
                            {getCurrencyFormat(missingBox.calculatedAmount)}
                          </td>
                        </tr>
                      ))}
                      {data.uniformDeduction &&
                        Number(
                          data.uniformDeduction.replace(/[^0-9.-]+/g, "")
                        ) > 0 && (
                          <tr
                            style={{
                              background: "#f8fafc",
                              borderBottom: "1px solid #e2e8f0",
                            }}
                          >
                            <td
                              style={{ padding: "6px 10px", fontWeight: 500 }}
                            >
                              Uniforme
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                                color: "#64748b",
                              }}
                            >
                              quincenal
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "center",
                                color: "#64748b",
                              }}
                            >
                              Desde ingreso
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "right",
                                color: "#64748b",
                              }}
                            >
                              {data.uniformDeduction}
                            </td>
                            <td
                              style={{
                                padding: "6px 10px",
                                textAlign: "right",
                                fontWeight: 600,
                                color: "#dc2626",
                              }}
                            >
                              {data.uniformDeduction}
                            </td>
                          </tr>
                        )}
                      {appliedNewDeductions.map((deduction, index) => (
                        <tr
                          key={`new-${index}`}
                          style={{
                            background: index % 2 === 0 ? "#fff" : "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <td style={{ padding: "6px 10px", fontWeight: 500 }}>
                            {deduction.name}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "center",
                              color: "#64748b",
                            }}
                          >
                            {deduction.frequency}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "center",
                              color: "#64748b",
                            }}
                          >
                            {deduction.startDate} - {deduction.endDate}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "right",
                              color: "#64748b",
                            }}
                          >
                            {getCurrencyFormat(Number(deduction.amount))}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#dc2626",
                            }}
                          >
                            {getCurrencyFormat(deduction.calculatedAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr
                        style={{
                          background:
                            "linear-gradient(135deg, #008080 0%, #006666 100%)",
                        }}
                      >
                        <td
                          colSpan={4}
                          style={{
                            padding: "8px 10px",
                            color: "#fff",
                            fontWeight: 600,
                            textAlign: "right",
                          }}
                        >
                          Total de Deducciones
                        </td>
                        <td
                          style={{
                            padding: "8px 10px",
                            color: "#fff",
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          {(() => {
                            const totalMissing =
                              appliedMissingBoxes.reduce(
                                (sum, m) => sum + m.calculatedAmount,
                                0
                              );
                            const totalNew = appliedNewDeductions.reduce(
                              (sum, d) => sum + d.calculatedAmount,
                              0
                            );
                            const uniform =
                              Number(
                                (data.uniformDeduction || "0").replace(
                                  /[^0-9.-]+/g,
                                  ""
                                )
                              ) || 0;
                            return getCurrencyFormat(
                              totalMissing + totalNew + uniform
                            );
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p style={paymentDetailEmptyTabStyle}>
                  Aún no hay deducciones registradas para este colaborador en el
                  período.
                </p>
              )}
            </div>
          )}

          {activeTab === "celebrity-days" && (
            <div>
              {getCelebrityDaysWorked().length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.72rem",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background:
                            "linear-gradient(135deg, #50B7C0 0%, #3a9ea6 100%)",
                        }}
                      >
                        {[
                          "Fecha",
                          "Día Festivo",
                          "Tipo de Bono",
                          "Salario Diario",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "8px 10px",
                              color: "#fff",
                              fontWeight: 600,
                              textAlign:
                                h === "Salario Diario" ? "right" : "center",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getCelebrityDaysWorked().map((celebrityDay, index) => (
                        <tr
                          key={index}
                          style={{
                            background: index % 2 === 0 ? "#fff" : "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "center",
                              fontWeight: 500,
                            }}
                          >
                            {formatPayrollDisplayDate(celebrityDay.date)}
                          </td>
                          <td
                            style={{ padding: "6px 10px", textAlign: "center" }}
                          >
                            {celebrityDay.title}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "center",
                              color: "#64748b",
                            }}
                          >
                            {celebrityDay.bonusType}
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#1e293b",
                            }}
                          >
                            {(() => {
                              if (celebrityDay.isNewSystem && celebrityDay.amount) {
                                // Para nuevos bonos, mostrar el monto directamente
                                return getCurrencyFormat(Number(celebrityDay.amount));
                              } else {
                                // Para legacy, calcular solo el bono adicional (multiplicador - 1)
                                const dailySalary = Number(
                                  data.dailySalary?.replace(/[^0-9.-]+/g, "") || 0
                                );
                                const multiplierMatch = celebrityDay.bonusType?.match(/Salario x(\d+\.?\d*)/);
                                if (multiplierMatch) {
                                  const multiplier = parseFloat(multiplierMatch[1]);
                                  const bonusAmount = dailySalary * (multiplier - 1);
                                  return getCurrencyFormat(bonusAmount);
                                }
                                return getCurrencyFormat(dailySalary);
                              }
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr
                        style={{
                          background:
                            "linear-gradient(135deg, #008080 0%, #006666 100%)",
                        }}
                      >
                        <td
                          colSpan={3}
                          style={{
                            padding: "8px 10px",
                            color: "#fff",
                            fontWeight: 600,
                            textAlign: "right",
                          }}
                        >
                          Total Días Festivos Trabajados
                        </td>
                        <td
                          style={{
                            padding: "8px 10px",
                            color: "#fff",
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          {getCurrencyFormat(
                            getCelebrityDaysTotalFromBreakdown()
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p style={paymentDetailEmptyTabStyle}>
                  Aún no hay días festivos trabajados para este colaborador en
                  el período.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentDetail;
